/**
 * The panel: Shadow DOM host, transcript, composer, confirmation cards.
 *
 * Accessibility here is not optional politeness. The moment we are on someone
 * else's page, their accessibility audit becomes our problem, so: focus trap
 * while open, Esc closes, focus returns to the trigger, aria-modal, and a live
 * region so a screen reader hears the answer stream in.
 */
import { clockLabel, dayLabel, fullLabel, isPlausibleTime, sameDay } from "./time.js";
import {
  deriveBrandTokens,
  deriveBubbleTokens,
  deriveSurfaceTokens,
  type BubbleRole,
} from "@verb/theme";
import { safeUrl, type Card } from "./cards.js";
import {
  buildFields,
  buildStepper,
  FIELDS_PER_STEP,
  type InputFieldSpec,
  type Stepper,
} from "./formFields.js";
import { isHandle, resolve as resolveFile } from "./files.js";
import { styles } from "./styles.js";
import { blobStyles, drawBlob, followPointer, registerBlobProperties, setMood, type Mood } from "./blob.js";
import { BUILD, VERSION } from "./version.js";
import type { SavedCard, SavedEntry } from "./storage.js";

/** Where the launcher sits, and which corner the panel unfolds from. */
export type LauncherPosition = "bottom-right" | "bottom-left" | "top-right" | "top-left";

/**
 * "panel": the current default, a full-height drawer docked to one edge.
 * "dialog": a centred, fixed-size box, closer to a Cmd+K palette than a
 * sidebar. See the note on #applyDisplayMode for why this is a real
 * second layout rather than a CSS variant of the first.
 */
export type DisplayMode = "panel" | "dialog";

/**
 * The animated ring behind the launcher and behind highlighted starters.
 *
 * "brand" cycles the site's own colour and cannot clash with a palette it is
 * made from. "rainbow" is the full spectrum and is loud on purpose; see the
 * note above the glow rules in styles.ts for why it took two asks to ship.
 */
export type Glow = "off" | "brand" | "rainbow";

/**
 * 🔴 THE SAME NUMBER THE SERVER USES, and it has to stay that way. `/v1/turn`
 *    rejects a longer message with `400 {"error":"Invalid request body"}`, and
 *    sseClient's readableError faithfully renders that straight into the
 *    panel. So before this existed, a user who pasted a long stack trace, in a
 *    support conversation, which is the exact situation this product is for,
 *    was shown a developer's validation string on somebody else's product.
 *
 *    The browser stops them at the limit instead. See turnBody in
 *    backend/src/routes/turn.ts; if that changes, change this.
 *
 * 🔴 4,000, DOWN FROM 8,000 ON 2026-08-26, ON VISHNU'S CALL. It was never a
 *    measured number in either direction. What 4,000 costs is the person
 *    pasting an unusually long log, and they now get told where the limit is
 *    while they are still holding the text, rather than after sending. What it
 *    buys is a smaller worst case on the one field of a public endpoint that
 *    reaches a model: an 8,000-character message is roughly 2,000 tokens of
 *    prompt somebody else is paying for, on every turn, from anybody who can
 *    load the customer's page.
 */
const MAX_MESSAGE = 4000;

/** How close to the limit before the count appears. Quiet until it is useful. */
const COUNTER_VISIBLE_AT = 500;

/**
 * "Powered by Verb", the one line of ours on a customer's page.
 *
 * 🔴 IT STAYS UNLESS THE PLAN SAYS IT MAY GO (Vishnu, 2026-10-01: "free
 *    visibility and better trust"). Page CSS cannot reach it: it lives in the
 *    shadow root, and only the launcher is a ::part. Page JAVASCRIPT can, so
 *    checkBranding puts it back when the panel opens and every few seconds
 *    while it is open, and tells index.ts, which reports the site once.
 *    Pro and above may switch it off; /v1/config says so as showBranding.
 */
const POWERED = `Powered by <a href="https://askverb.com" target="_blank" rel="noopener">Verb</a>`;
const POWERED_TEXT = "Powered by Verb";
const BRANDING_CHECK_MS = 3000;

/** Long enough that the bubble reads as arriving, not as part of the page. */
const TEASER_DELAY_MS = 2500;
/**
 * How long the bubble stays quiet once the visitor closed it with its ×.
 *
 * 🔴 A DAY, NOT A MONTH (Vishnu, 2026-10-27: "if it doesn't come, it loses its purpose"). It
 *    shows once per visit anyway (the session key below), so a reload never repeats it; the
 *    usual practice for these prompts is once per session with a daily cap after a dismissal.
 *    A month meant one click on × hid it for good as far as anyone testing could tell. Opening
 *    the panel no longer silences it at all: see open().
 */
const TEASER_QUIET_MS = 24 * 60 * 60 * 1000;
/**
 * 🔴 TWO KEYS, BECAUSE "SEEN" AND "ANSWERED" ARE DIFFERENT THINGS.
 *
 *    The session key stops a reload repeating it inside one visit. The local
 *    key holds the timestamp it may speak again, written only when the visitor
 *    actually answered it. Ignoring it is not answering it, so an ignored
 *    bubble costs nothing beyond the current tab.
 *
 *    Both are per origin, so they are already scoped to the customer's own
 *    site without us putting their id in them.
 *
 * 🔴 THE LOCAL KEY IS DELIBERATELY A NEW NAME. The old one stored "1" and
 *    meant forever, and a bug before widget 0.0.20 wrote it on every open(),
 *    so a great many browsers are marked permanently seen for a bubble they
 *    were never shown. Reading a different key retires all of those at once
 *    rather than stranding them (2026-10-07).
 */
const TEASER_QUIET_KEY = "verb_teaser_quiet_until";
const TEASER_SESSION_KEY = "verb_teaser_seen";

/**
 * One row of a confirmation card, built by the server for a PERSON to read.
 *
 * 🔴 THE CARD IS A LIST OF THESE, NOT THE ARGUMENTS. It used to print
 *    `Object.entries(args)`: a humanised key over the raw value, so "Booking uid" sat
 *    above the uid itself. Whatever the server decides is worth showing arrives
 *    already in words, and the widget formats it and nothing more. docs/56, phase 5.
 */
export interface DisplayRow {
  readonly label: string;
  readonly value: string;
  /** An ISO timestamp the widget shows in the visitor's own date and time format. */
  readonly kind?: "datetime" | "date";
}

/** The real arguments, which the server sends ONLY for a test site's builders. docs/56, phase 6. */
export interface DebugInfo {
  readonly action: string;
  readonly args: unknown;
}

/** A row's value as the visitor should read it: a timestamp in their own language and zone. */
function formatRow(row: DisplayRow): string {
  if (row.kind !== "datetime" && row.kind !== "date") return row.value;
  const when = new Date(row.value);
  if (Number.isNaN(when.getTime())) return row.value;
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "full",
      ...(row.kind === "datetime" ? { timeStyle: "short" } : {}),
    } as Intl.DateTimeFormatOptions).format(when);
  } catch {
    return row.value;
  }
}

export interface PanelCallbacks {
  onSend(message: string): void;
  /** `values` is present only when the card carried fields the user filled in. */
  onConfirm(callId: string, decision: "allow" | "deny", values?: Record<string, unknown>): void;
  /** A collect-only card on a read tool. `null` means the user dismissed it. */
  onInput(callId: string, values: Record<string, unknown> | null): void;
  onOpen(): void;
  /**
   * A user rewrote something they already sent.
   *
   * `rewindTurns` is how many COMMITTED turns to unwind, counted from the edited
   * message onwards. Without it the server would still hold the original
   * question and its answer, and the model would answer the edit with the
   * mistake still in front of it.
   */
  onEdit(message: string, rewindTurns: number, replaces: string): void;
  /**
   * A starter with a written answer was clicked, so the exchange on screen
   * never went near the model or the server.
   *
   * 🔴 IT EXISTS BECAUSE NOTHING ELSE WOULD SAVE THE TRANSCRIPT. Every other
   *    message lands in sessionStorage from the send loop's `finally`. A
   *    written answer has no send loop, so without this the visitor reloads
   *    the page and the conversation they were reading is gone.
   */
  onAnswered(): void;
  /**
   * The user asked to start over.
   *
   * 🔴 THE HALF THAT IS NOT ON SCREEN IS THE IMPORTANT HALF. Clearing the
   *    transcript is cosmetic: the server still holds every earlier turn under
   *    the same conversation id, so without dropping it here the "new" chat
   *    would be answered with the old one still in front of the model, and
   *    nothing on screen would explain why.
   */
  onNewChat(): void;
  /**
   * Somebody rated an answer.
   *
   * 🔴 FIRE AND FORGET, AND THE PANEL DOES NOT WAIT. A rating is a courtesy the
   *    visitor did us; making them watch a spinner for it, or telling them it
   *    failed, spends their attention on our problem. The thumb goes solid
   *    immediately and index.ts posts in the background.
   */
  onFeedback(messageId: string, rating: "up" | "down", comment?: string): void;
}

/** One card in the opening row. `answer` makes it cost nothing: see showOpening. */
export interface Starter {
  title: string;
  /** Absent on a link card, which sends nothing. See linkStarter below. */
  prompt?: string;
  answer?: string;
  /** Opens a link instead of asking anything. See linkStarter below. */
  url?: string;
  /** Draws the eye to one card. */
  highlight?: boolean;
}

/**
 * Whether a starter is a link, and whether that link is one we will render.
 *
 * 🔴 CHECKED HERE AS WELL AS ON SAVE, AND THAT IS NOT BELT AND BRACES. This
 *    value goes into an href on the CUSTOMER'S OWN PAGE. The dashboard refuses
 *    anything but http and https today, but rows saved before that check
 *    existed do not re-validate themselves, and a `javascript:` URL reaching
 *    this line would be stored XSS that we served to them. A starter whose URL
 *    does not parse as http or https simply behaves as a normal card.
 */
function linkStarter(s: Starter): string | null {
  if (!s.url) return null;
  try {
    const u = new URL(s.url);
    return u.protocol === "https:" || u.protocol === "http:" ? u.href : null;
  } catch {
    return null;
  }
}

export class Panel {
  readonly host: HTMLElement;
  readonly #shadow: ShadowRoot;
  readonly #panel: HTMLElement;
  /** Removes the visual-viewport listeners while the panel is closed. */
  #untrackViewport: (() => void) | null = null;
  /** Gives the host page its scroll back; set only while a phone-sized sheet is open. */
  #unlockPage: (() => void) | null = null;
  readonly #launcher: HTMLButtonElement;
  readonly #teaser: HTMLElement;
  readonly #sheet: CSSStyleSheet;
  #sheetRules = 0;
  readonly #footer: HTMLElement;
  readonly #footerHome: HTMLElement;
  /** True only when the site's plan allows removing it and the owner did. */
  #brandingOff = false;
  #brandingTimer: ReturnType<typeof setInterval> | null = null;
  /** Called after checkBranding had to put the line back. index.ts reports it. */
  onBrandingRestored: (() => void) | null = null;
  readonly #scrim: HTMLElement;
  readonly #pendingBar: HTMLElement;
  readonly #transcript: HTMLElement;
  readonly #input: HTMLTextAreaElement;
  readonly #send: HTMLButtonElement;
  readonly #live: HTMLElement;
  readonly #newChatBtn: HTMLButtonElement;
  readonly #counter: HTMLElement;

  #open = false;
  /** Whether the input had focus when the current turn started. See setBusy. */
  #typing = false;
  #streaming: HTMLElement | null = null;
  /**
   * What the panel opened with, kept so "New chat" can put it back.
   *
   * The greeting and the cards arrive once, from /v1/config, long before
   * anybody presses anything. Re-fetching them to start a second conversation
   * would mean a network round trip to redraw something we already had, and a
   * blank panel if it failed.
   */
  #opening: { greeting: string; starters: readonly Starter[] } | null = null;
  /**
   * Which written answer is currently being played out.
   *
   * 🔴 A WRITTEN ANSWER TAKES ABOUT A SECOND TO ARRIVE, and "New chat" during
   *    that second must actually stop it. Without this the loop keeps calling
   *    appendDelta into a transcript that has been cleared, and the answer to
   *    a question nobody can see reappears line by line.
   */
  #answerRun = 0;
  #lastFocus: Element | null = null;
  #dryRun = false;
  /** Hidden until a token arrives, on sites that declared they are behind a login. */
  #hidden = false;
  /** The "working on it" row, if one is on screen. At most one ever exists. */
  #thinking: HTMLElement | null = null;
  #assistantName = "";
  #avatarUrl = "";
  /** A blob's seed (migration 0067). Wins over #avatarUrl while set. */
  #avatarBlob = "";
  /** What the moving blobs' faces say now; a new one is drawn wearing it. */
  #mood: Mood = "idle";
  #moodTimer: ReturnType<typeof setTimeout> | null = null;
  /** How this turn's actions went, shown when the answer starts: happy if done, unsure if not. */
  #turnMood: Mood | null = null;
  /**
   * Wear the avatar on the launcher instead of our mark (migration 0057).
   *
   * 🔴 OFF UNLESS THE SITE ASKED. Every site with an avatar would otherwise
   *    have had its button change on the next deploy, which is an appearance
   *    change to somebody else's live page that nobody requested.
   */
  #launcherAvatar = false;
  /** How big that face is, as a multiple of the 52px it was (migration 0068); 1.75 unless told. */
  #launcherSize = 1.75;
  #teaserText: string | null = null;
  #teaserTimer: ReturnType<typeof setTimeout> | null = null;
  #teaserShown = false;
  #pushLayout: boolean;
  readonly #panelWidth = "400px";
  #displayMode: DisplayMode = "panel";
  /** Config-driven, permanent: distinct from #hidden, which is identity-gating. */
  #showLauncher = true;

  constructor(
    private readonly cb: PanelCallbacks,
    brandName: string,
    opts: {
      pushLayout?: boolean;
      position?: LauncherPosition;
      displayMode?: DisplayMode;
      showLauncher?: boolean;
      brandColor?: string;
      backgroundColor?: string;
      glow?: Glow;
      bubbleUserColor?: string;
      bubbleAssistantColor?: string;
    } = {},
  ) {
    // Default true: pushing the site's own content aside, not overlaying it,
    // is now what happens out of the box. See #applyPush for how, and why this
    // can still be turned off per site.
    this.#pushLayout = opts.pushLayout ?? true;
    this.#assistantName = brandName;
    this.host = document.createElement("div");
    this.host.setAttribute("data-verb-root", "");
    /**
     * The host is the ONLY element we add to their DOM, and it must take up no
     * space and affect no layout.
     *
     * 🔴 IT CARRIES NO Z-INDEX, AND IT USED TO. `z-index` has no effect on a
     *    `position: static` element, so the value here was inert while looking
     *    exactly like the thing that would have prevented a customer's sticky
     *    header from drawing over our panel. The real stacking lives on the
     *    fixed `.launcher` and `.panel` inside the shadow root, where their
     *    stylesheet cannot reach it. See styles.ts.
     */
    this.host.style.cssText = "position:static;";

    this.#shadow = this.host.attachShadow({ mode: "open" });
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styles + blobStyles);
    this.#shadow.adoptedStyleSheets = [sheet];
    this.#sheet = sheet;
    this.#sheetRules = sheet.cssRules.length;

    this.#shadow.innerHTML = `
      <button class="launcher" part="launcher" aria-haspopup="dialog" aria-expanded="false">
        <!-- The glow's three layers. They are here for every site, invisible
             until data-glow is set, so switching it on is an opacity change on
             something already in the tree rather than a repaint of the button.
             The <i> in each is the square that spins; see styles.ts. -->
        <span class="launcher-halo" aria-hidden="true"><i></i></span>
        <span class="launcher-ring" aria-hidden="true"><i></i></span>
        <span class="launcher-face" aria-hidden="true"></span>
        <!-- Wrapped, not loose: the layers above are positioned, and
             positioned elements paint above bare inline content. -->
        <span class="launcher-label">
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M8 1.5 9.7 6.3 14.5 8 9.7 9.7 8 14.5 6.3 9.7 1.5 8 6.3 6.3z" fill="currentColor"/>
          </svg>
          <span class="launcher-text">Ask AI</span>
        </span>
      </button>
      <div class="teaser" hidden>
        <button class="teaser-open" type="button"></button>
        <button class="teaser-close" type="button" aria-label="Not now">&times;</button>
      </div>
      <div class="panel" role="dialog" aria-modal="true" aria-label="${escapeHtml(brandName)} assistant" data-open="false">
        <div class="header">
          <span class="header-id">
            <span class="avatar avatar-header" aria-hidden="true"></span>
            <span class="title">${escapeHtml(brandName)}</span>
          </span>
          <span class="header-actions">
            <button class="newchat" aria-label="New chat" title="New chat">
              <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true">
                <path d="M8 3.5v9M3.5 8h9" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
              </svg>
            </button>
            <button class="close" aria-label="Close assistant">
              <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true">
                <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
              </svg>
            </button>
          </span>
        </div>
        <div class="transcript" role="log"><p class="empty">Ask a question, or ask me to do something.</p></div>
        <div class="sr-only" aria-live="polite" aria-atomic="false"></div>
        <!-- 🔴 THE WAY BACK TO A CONFIRMATION THAT SCROLLED AWAY. The composer
             is disabled for the whole time a card is waiting, which is correct:
             a second message into an open turn has nowhere sane to go. What was
             missing is any way to reach the card once it left the viewport. A
             dead input and no visible reason is indistinguishable from the
             product having crashed, and on 2026-09-08 that is exactly how a
             demo read it: "very unresponsive", followed by a logout. -->
        <button class="pending-bar" hidden>
          <span class="pending-dot" aria-hidden="true"></span>
          <span class="pending-text">Waiting for you to approve an action</span>
          <span class="pending-cta">Show it</span>
        </button>
        <div class="composer">
          <textarea class="input" rows="1" maxlength="${MAX_MESSAGE}" placeholder="Ask anything…" aria-label="Message"></textarea>
          <button class="send">Send</button>
          <span class="counter" aria-live="polite" hidden></span>
        </div>
        <div class="footer">
          <div class="powered">${POWERED}</div>
          <div class="build" title="Build ${BUILD}">v${VERSION}</div>
        </div>
      </div>
      <!-- 🔴 THE BACKDROP, DIALOG MODE ONLY. Its own element rather than a
           pseudo-element on the panel, because a ::before on a transformed,
           scaled box scales with it and cannot cover the viewport. It sits
           after the panel in source and below it in z-index, so no stacking
           accident can put it on top of the thing it exists to sit behind. -->
      <div class="scrim" data-open="false" aria-hidden="true"></div>`;

    this.#panel = this.#q(".panel");
    this.#footer = this.#q(".footer");
    this.#footerHome = this.#footer.parentElement!;
    this.#pendingBar = this.#q(".pending-bar");
    this.#scrim = this.#q(".scrim");
    this.#launcher = this.#q(".launcher");
    this.#teaser = this.#q(".teaser");
    this.#transcript = this.#q(".transcript");
    this.#input = this.#q(".input");
    this.#send = this.#q(".send");
    this.#live = this.#q(".sr-only");
    this.#newChatBtn = this.#q(".newchat");
    this.#counter = this.#q(".counter");

    // The header's avatar ships as an empty span in the markup above, because
    // building an <img> or an initial there means running the same logic the
    // avatar builder already owns. One call fills it in.
    this.#repaintAvatars();

    this.#launcher.addEventListener("click", () => this.open());
    /**
     * 🔴 CLICKING THE BACKDROP CLOSES, because every modal a visitor has used
     *    behaves that way and one that does not reads as stuck. Escape already
     *    did this; the scrim is the same gesture for somebody using a mouse.
     */
    this.#scrim.addEventListener("click", () => this.close());
    this.#pendingBar.addEventListener("click", () => this.revealPendingConfirm());
    this.#q<HTMLButtonElement>(".close").addEventListener("click", () => this.close());
    /**
     * 🔴 ALWAYS VISIBLE, AND IT SHIPPED HIDDEN FOR ABOUT AN HOUR.
     *
     *    The first version only appeared once the user had sent a message, on
     *    the reasoning that offering "new chat" above a greeting and a row of
     *    cards is offering to replace a panel with itself. True, and beside
     *    the point: Vishnu opened his own admin panel looking for the feature
     *    he had just asked for, saw only the close button, and reasonably
     *    concluded the deploy had not reached him.
     *
     *    A control that hides until you have already done the thing it helps
     *    with cannot be found by the person looking for it. On an empty panel
     *    it redraws the opening, which is a no-op the user can see is a no-op,
     *    and that is a far cheaper cost than being invisible.
     */
    this.#teaser.querySelector(".teaser-open")!.addEventListener("click", () => this.open());
    this.#teaser.querySelector(".teaser-close")!.addEventListener("click", () => this.#dismissTeaser(true));

    this.#newChatBtn.addEventListener("click", () => this.newChat());
    this.#send.addEventListener("click", () => this.#submit());

    this.#input.addEventListener("keydown", (e) => {
      // Enter sends, Shift+Enter is a newline. Matches every chat surface a user
      // has ever touched.
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        this.#submit();
      }
    });
    this.#input.addEventListener("input", () => {
      this.#input.style.height = "auto";
      this.#input.style.height = `${Math.min(this.#input.scrollHeight, 120)}px`;
      this.#syncCounter();
    });

    this.#shadow.addEventListener("keydown", (e) => {
      const ev = e as KeyboardEvent;
      if (ev.key === "Escape" && this.#open) {
        ev.stopPropagation();
        this.close();
      }
      if (ev.key === "Tab" && this.#open) this.#trapFocus(ev);
    });

    /**
     * Keys typed into the panel stay in the panel.
     *
     * 🔴 A KEY EVENT IS COMPOSED, SO IT LEAVES THE SHADOW ROOT AND THE HOST
     *    PAGE ACTS ON IT (cal.askverb.com, 2026-10-06: typing "what apps do I
     *    have" navigated to cal.com's Profile page, because "p" then "s" is one
     *    of KBar's two-key shortcuts). Those libraries do guard against firing
     *    while somebody types, by asking whether document.activeElement is a
     *    field, and shadow DOM makes that answer our HOST element rather than
     *    our textarea inside it, so they conclude nobody is typing. Nothing we
     *    can set on our side changes their answer: the keystroke must not reach
     *    them at all. Any host app with single-key or sequence shortcuts behaves
     *    the same way (GitHub, Gmail, Linear, Notion, Jira), which is why this
     *    is fixed here once rather than on any one customer's site.
     *
     * 🔴 ONLY WHERE THE KEY WAS ACTUALLY TYPED. composedPath()[0] is the real
     *    target inside the shadow tree, which retargeting hides from `target`.
     *    Buttons and confirmation cards are deliberately left alone, so a host
     *    page's own keyboard navigation keeps working everywhere except inside
     *    a field of ours. This covers every editable thing the widget renders,
     *    not just the composer: the feedback note, the edit-a-question box and
     *    every field built by formFields all live under this same shadow root.
     *
     * 🔴 THE PANEL'S OWN HANDLERS ARE UNAFFECTED. They sit on the fields
     *    themselves and on this same shadow root, so all of them have already
     *    run by the time propagation stops here: Enter sends, Shift+Enter breaks
     *    the line, Escape closes, and Tab stays trapped.
     */
    for (const type of ["keydown", "keyup", "keypress"]) {
      this.#shadow.addEventListener(type, (e: Event) => {
        const typed = e.composedPath()[0] as HTMLElement | undefined;
        if (!typed) return;
        if (typed.tagName === "TEXTAREA" || typed.tagName === "INPUT" || typed.isContentEditable) {
          e.stopPropagation();
        }
      });
    }

    // Applied here rather than left to defaults, so a script-tag hint takes
    // effect on the very first paint, before any config has had time to load.
    if (opts.position) this.setPosition(opts.position);
    if (opts.displayMode) this.setDisplayMode(opts.displayMode);
    if (opts.showLauncher !== undefined) this.setLauncherVisible(opts.showLauncher);
    if (opts.brandColor) this.setBrandColor(opts.brandColor);
    if (opts.backgroundColor) this.setBackgroundColor(opts.backgroundColor);
    if (opts.glow) this.setGlow(opts.glow);
    if (opts.bubbleUserColor) this.setBubbleColor("user", opts.bubbleUserColor);
    if (opts.bubbleAssistantColor) this.setBubbleColor("assistant", opts.bubbleAssistantColor);
  }

  /**
   * Reveal the launcher, once its colours are settled.
   *
   * 🔴 IDEMPOTENT AND ONE-WAY ON PURPOSE. It is called from two places that
   *    race: whichever of the config response and the safety timer arrives
   *    first wins, and the second is a no-op. There is no path back to hidden,
   *    because a launcher that could disappear again is worse than one that
   *    appeared in the wrong colour.
   */
  markReady(): void {
    this.#launcher.dataset["ready"] = "true";
  }

  /**
   * Move the launcher out of the floating corner and into an element of the
   * customer's own, named by a CSS selector.
   *
   * 🔴 IT GETS ITS OWN SHADOW ROOT RATHER THAN BEING APPENDED RAW. The button
   *    has to keep our stylesheet, and our stylesheet lives in a shadow root;
   *    dropping the bare element into their DOM would hand it to their CSS,
   *    which is the one thing shadow DOM is here to prevent. A second host
   *    with the same adopted sheet keeps the isolation, and their layout
   *    positions the host like any other child.
   *
   * 🔴 A SELECTOR THAT MATCHES NOTHING LEAVES THE FLOATING BUTTON ALONE. The
   *    alternative is a site with no way to open the assistant at all because
   *    somebody renamed a div, and a widget that vanishes is worse than one in
   *    a corner they did not choose. It warns instead, naming the selector.
   */
  /**
   * Moves the floating button away from its corner, in pixels, for a corner
   * already taken by a cookie banner or another chat bubble. From
   * data-verb-offset-x / -y; a fact about their page, like data-verb-launcher,
   * so script tag only. Applies on phones as well.
   */
  setLauncherOffset(x: number | undefined, y: number | undefined): void {
    if (x !== undefined) this.#launcher.style.setProperty("--verb-offset-x", `${x}px`);
    if (y !== undefined) this.#launcher.style.setProperty("--verb-offset-y", `${y}px`);
  }

  /** Whether the button currently sits inside a live element of the customer's. */
  launcherPlaced(): boolean {
    return this.#launcher.dataset["inline"] === "true" && this.#launcher.isConnected;
  }

  /**
   * Takes back a question that never left the browser, and puts the words back
   * in the composer so nothing has to be typed twice.
   *
   * 🔴 ONLY FOR A QUESTION THE SERVER NEVER RECEIVED: offline at send, or a
   *    request that failed before connecting. A question the server did get is
   *    part of the conversation, and taking its bubble away would make the
   *    screen disagree with the history.
   */
  returnDraft(text: string): void {
    const rows = this.#transcript.querySelectorAll<HTMLElement>(".row");
    const last = rows[rows.length - 1];
    if (last?.dataset["role"] === "user" && last.querySelector(".msg")?.textContent === text) last.remove();
    if (!this.#input.value.trim()) {
      this.#input.value = text;
      this.#syncCounter();
    }
  }

  mountLauncherInto(selector: string, quiet = false): boolean {
    let target: Element | null = null;
    try {
      target = document.querySelector(selector);
    } catch {
      // An invalid selector throws rather than returning null.
      target = null;
    }
    if (!target) {
      if (quiet) return false;
      console.warn(
        `[Verb] data-verb-launcher="${selector}" matched no element, so the ` +
          `button stayed in its corner. Check the selector, and that the ` +
          `element exists by the time the script runs.`,
      );
      return false;
    }

    const host = document.createElement("span");
    host.setAttribute("data-verb-launcher-root", "");
    // display:contents so the host adds no box of its own: whatever layout the
    // customer applies to their container applies to the button directly.
    host.style.cssText = "display:contents;";
    // No key-leak guard on this one, unlike the panel's shadow root above: the
    // only thing that ever moves in here is the launcher button, and a key
    // pressed on a button is the host page's business, not ours.
    const shadow = host.attachShadow({ mode: "open" });
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styles + blobStyles);
    shadow.adoptedStyleSheets = [sheet];

    this.#launcher.dataset["inline"] = "true";
    shadow.appendChild(this.#launcher);
    target.appendChild(host);
    return true;
  }

  mount(parent: HTMLElement = document.body): void {
    parent.appendChild(this.host);
    /**
     * 🔴 THE FLOOR UNDER markReady, NOT THE MECHANISM. index.ts reveals the
     *    launcher the moment /v1/config settles, success or failure, which on
     *    any normal connection is faster than this. This exists for the case
     *    that call never settles at all: a hung request, a blocked origin, an
     *    extension eating the fetch. Without it the button would simply never
     *    appear, which is a far worse failure than the repaint it replaced.
     */
    setTimeout(() => this.markReady(), 1000);
    // A number, not a fact about the customer's layout: it is read by THEIR
    // stylesheet, if they choose to, in the opt-in below. Setting it always
    // costs nothing on a page that never references it.
    document.documentElement.style.setProperty("--verb-panel-width", this.#panelWidth);
  }

  /**
   * Never called on a timer or on scroll. An assistant that opens unprompted is
   * the thing everyone hates about chat widgets, and it is a reason to remove us.
   */
  /**
   * Open the panel and SEND the question, rather than only typing it in.
   *
   * 🔴 THE DIFFERENCE FROM open({ prompt }) IS THE WHOLE POINT. `open` puts
   *    text in the box and leaves it there, which is right when a page wants
   *    to suggest a question. It is wrong when somebody has already typed
   *    their question somewhere else on the page and pressed enter: making
   *    them press send a second time, on a box they can see already contains
   *    what they wrote, reads as the first press having failed.
   *
   * Works whether the panel is open or closed, because a host page's own input
   * does not know or care which.
   */
  ask(text: string): void {
    const question = text.trim();
    if (!question) return;
    if (!this.#open) this.open();
    this.#input.value = question;
    // Through #submit, not straight to onSend, so this is byte-for-byte the
    // path the composer takes: the transcript row, the empty box and the
    // busy guard are all one implementation rather than two that drift.
    this.#submit();
  }

  open(prompt?: string): void {
    if (this.#open) return;
    this.#open = true;
    this.#lastFocus = document.activeElement;
    this.#panel.dataset["open"] = "true";
    // Opening IS the thing the bubble was asking for, so it goes for this visit. Never remembered
    // past it (2026-10-27): somebody who used the assistant is the person most worth reminding
    // next time, and a month of silence after one chat hid the bubble from every returning user.
    this.#dismissTeaser(false);
    this.#trackViewport();
    this.#lockPage();
    this.#syncScrim();
    this.#launcher.setAttribute("aria-expanded", "true");
    this.#syncLauncherVisibility();
    this.#markPageState(true);
    this.checkBranding();
    if (this.#brandingTimer === null) {
      this.#brandingTimer = setInterval(() => this.checkBranding(), BRANDING_CHECK_MS);
    }
    if (prompt) this.#input.value = prompt;

    /**
     * 🔴 REOPENING ONTO A DISABLED INPUT WITH NO EXPLANATION READ AS "BROKEN".
     *
     *    The input stays disabled for the entire time a tool call is waiting
     *    on a human, by design: the turn is still open server-side, and typing
     *    a second message into it would not go anywhere sane. Close the panel
     *    while that card is showing, and the card can be scrolled out of view
     *    or simply forgotten. Reopen, and all that is visible is a greyed-out
     *    box that does nothing when you type into it, which is indistinguishable
     *    from something having failed.
     *
     *    So on reopen, if a confirmation is still waiting, land on IT, focused
     *    on the safe option, exactly like the moment it first appeared. The
     *    input was never broken; it was correctly disabled and the reason for
     *    that was off screen.
     */
    const pending = this.#pendingConfirm();
    if (pending) {
      pending.scrollIntoView({ block: "nearest" });
      pending.querySelector<HTMLButtonElement>(".btn-secondary")?.focus();
    } else {
      this.#input.focus();
    }
    this.cb.onOpen();
  }

  #pendingConfirm(): HTMLElement | null {
    return this.#shadow.querySelector<HTMLElement>('.confirm:not([data-resolved="true"])');
  }

  /**
   * Scroll the waiting confirmation back into view and focus the safe option.
   *
   * Public because the bar above the composer calls it, and because a host
   * page that has built its own affordance should be able to as well.
   */
  revealPendingConfirm(): void {
    const card = this.#pendingConfirm();
    if (!card) return;

    /**
     * 🔴 "center", NOT "nearest", AND A FLASH REGARDLESS. `block: "nearest"`
     *    is a no-op when the element is ALREADY inside the scrollport, so
     *    pressing Show it while the card was on screen did visibly nothing and
     *    read as a dead button. Reported twice.
     *
     *    Centring always moves something unless the card is already dead
     *    centre, and the flash covers even that case: the point of the press is
     *    "which thing is waiting for me", and an answer you cannot see is not
     *    an answer.
     */
    card.scrollIntoView({ block: "center", behavior: "smooth" });
    card.dataset["flash"] = "true";
    window.setTimeout(() => card.removeAttribute("data-flash"), 1200);
    card.querySelector<HTMLButtonElement>(".btn-secondary")?.focus();
  }

  /**
   * 🔴 THE BAR IS DERIVED FROM THE DOM, NEVER FROM A FLAG. There is exactly one
   *    source of truth for "is a confirmation waiting", and it is whether an
   *    unresolved card exists. A parallel boolean would be a second one, and
   *    the failure mode of the two disagreeing is a permanently stuck composer
   *    with a bar pointing at a card that is not there.
   */
  #syncPendingBar(): void {
    this.#pendingBar.hidden = this.#pendingConfirm() === null;
  }

  close(): void {
    if (!this.#open) return;
    this.#open = false;
    this.#panel.dataset["open"] = "false";
    this.#releasePhone();
    this.#syncScrim();
    this.#launcher.setAttribute("aria-expanded", "false");
    // Never bring back a launcher that is meant to stay hidden.
    this.#syncLauncherVisibility();
    this.#markPageState(false);
    if (this.#brandingTimer !== null) {
      clearInterval(this.#brandingTimer);
      this.#brandingTimer = null;
    }
    // Focus returns where it came from. Dumping focus at the top of their page
    // is a WCAG failure and it is disorienting even if nobody files it.
    (this.#lastFocus instanceof HTMLElement ? this.#lastFocus : this.#launcher).focus();
  }

  /**
   * 🔴 PUSH IS THE DEFAULT NOW, NOT AN OPT-IN. THIS IS A DELIBERATE, NAMED RISK.
   *
   *    The earlier version left the site's own layout untouched and made a
   *    customer write CSS to turn pushing on. That was the cautious answer to
   *    "we cannot see a stranger's stylesheet," and it produced a widget that,
   *    by default, covers whatever is under it on every single install, which
   *    is the complaint that started this: mirchi35's own business table, cut
   *    off, on first load, on every reload, until someone went and wrote CSS
   *    nobody had told them to write.
   *
   *    So `margin-right` is now applied directly to `<body>`, in JS, on every
   *    site, unless that site opts out. `!important` on the way in, because a
   *    stylesheet rule as ordinary as `body { margin: 0 !important; }` exists
   *    on real sites and would otherwise silently win. Removed entirely, not
   *    zeroed, on the way out, so whatever the site's own CSS says resumes
   *    governing the moment we are not touching it.
   *
   *    This still cannot be correct on every possible layout, and never
   *    claims to be. A page whose content is sized in `vw` rather than `%`
   *    ignores a parent's margin by definition; that page gets a horizontal
   *    scrollbar instead of a shift, not broken content. `data-verb-layout=
   *    "overlay"` on the script tag turns this off entirely and returns to
   *    the old covering behaviour, for the one site where that trade is
   *    wrong.
   *
   *    Never applied under 640px: the panel itself goes full-width there (see
   *    styles.ts), so there is no page beside it left to push.
   *
   * `data-verb-panel-open` and `--verb-panel-width` are still set regardless
   * of push being on, because a site that wants to push something OTHER than
   * `<body>`, one specific wrapper div instead, still needs a hook to react to
   * and a width to react with. Turning off our own push does not remove
   * theirs.
   */
  #markPageState(open: boolean): void {
    document.documentElement.dataset["verbPanelOpen"] = String(open);

    // A centred dialog has nothing beside it to push: it does not dock to an
    // edge, so a margin change on <body> would be motion with no purpose.
    if (!this.#pushLayout || this.#displayMode === "dialog" || window.innerWidth <= 640) return;

    // The transition is set once and left there, never removed alongside
    // margin-right: removing both in the same tick would drop the transition
    // before the browser paints the margin change, so CLOSING would jump
    // instead of animate. Left in place, it is inert on any margin-right
    // change that is not ours, which is the one acceptable cost of it.
    document.body.style.setProperty("transition", "margin-right 180ms cubic-bezier(.32,.72,0,1)");

    if (open) {
      document.body.style.setProperty("margin-right", this.#panelWidth, "important");
    } else {
      document.body.style.removeProperty("margin-right");
    }
  }

  get isOpen(): boolean {
    return this.#open;
  }

  /**
   * One message: an avatar row for the assistant, an editable row for the user.
   *
   * 🔴 THE AVATAR IS A SIBLING OF THE BUBBLE, NOT A CHILD OF IT. `.msg` holds the
   *    message text and nothing else, so reading its textContent still yields
   *    exactly what was said. Putting an initial inside it would have quietly
   *    prefixed every answer with a letter everywhere we read it back: the live
   *    region a screen reader announces, and the text an edit restores.
   *
   * Returns the bubble, because that is what streaming writes into.
   */
  addMessage(role: "user" | "assistant", text: string, at: number | null = Date.now()): HTMLElement {
    this.#q<HTMLElement>(".empty")?.remove();
    // A new question starts a new group, so one turn's tools never land in the
    // previous turn's summary. Belt and braces with finishTurn: this also
    // covers an edit, which re-sends without the send loop's finally having
    // run for the turn it replaces.
    if (role === "user") this.#toolGroup = null;

    const row = document.createElement("div");
    row.className = "row";
    row.dataset["role"] = role;

    /**
     * 🔴 WHEN IT WAS SAID, AS ONE UTC INSTANT, SHOWN IN THE VISITOR'S OWN TIME. `at` is epoch
     *    milliseconds and is the only thing stored; every word on screen about it comes from
     *    time.ts through the browser's Intl, so a timezone is never worked out by hand.
     *    `null` is for a line that was never said by anybody, such as the greeting, which gets
     *    no time and no day heading.
     */
    if (at !== null) {
      this.#placeDay(at);
      row.dataset["at"] = String(at);
    }

    if (role === "assistant") row.appendChild(this.#avatar());

    const el = document.createElement("div");
    el.className = "msg";
    el.dataset["role"] = role;
    if (role === "assistant") {
      // The model writes **like this** out of habit. Rendered, not stripped:
      // see renderRich for why that is still never innerHTML.
      el.dataset["raw"] = text;
      renderRich(el, text);
    } else {
      el.textContent = text; // textContent, never innerHTML, model output is not markup
    }
    row.appendChild(el);

    /**
     * 🔴 AFTER the bubble, not before it. A user row is justified to the right,
     *    so the avatar belongs on the outside edge, which is the right. Putting
     *    it first would park it in the middle of the panel with the bubble
     *    pushed further right still.
     */
    if (role === "user") row.appendChild(this.#userAvatar());

    // Its own line under the bubble. A sibling, never inside `.msg`, for the same reason the
    // avatar is: what is read back from the bubble must be exactly what was said.
    if (at !== null) row.appendChild(this.#stamp(at));

    /**
     * 🔴 EDITING IS SWITCHED OFF FOR THE BETA, 2026-08-20, ON VISHNU'S CALL.
     *
     *    Commented out HERE, at the one line that renders it, rather than
     *    deleted: `#editButton`, `#beginEdit`, `#committedTurnsFrom`,
     *    `commitTurn` and the `onEdit` callback all still exist and still
     *    work, and the server's `rewind_turns` half was never the doubtful
     *    part. Putting the button back is uncommenting this line.
     *
     *    Kept rather than removed because the hard-won part is the branch
     *    handling: an edit cannot be a cosmetic undo, since leaving the
     *    original question and its answer in the server's history makes the
     *    model answer the old one. That reasoning is on #editButton below and
     *    is worth more than the button.
     */
    // if (role === "user") row.appendChild(this.#editButton(row, el));

    this.#transcript.appendChild(row);
    // The thinking row stays last, so a tool chip or a partial answer never
    // appears below the thing that says we are still working.
    if (this.#thinking) this.#transcript.appendChild(this.#thinking);
    this.#refreshDays();
    this.#scroll();
    return el;
  }

  /** `<time>`: the machine-readable instant for anything that reads the page, the local clock for the person. */
  #stamp(at: number): HTMLElement {
    const el = document.createElement("time");
    el.className = "stamp";
    el.dateTime = new Date(at).toISOString();
    el.textContent = clockLabel(at);
    // The one place the timezone is named, so the short label can stay short and still be unambiguous.
    el.title = fullLabel(at);
    return el;
  }

  /** A "Today" / "Yesterday" / date heading before the first message of a new calendar day. */
  #placeDay(at: number): void {
    const rows = this.#transcript.querySelectorAll<HTMLElement>(".row[data-at]");
    const previous = Number(rows[rows.length - 1]?.dataset["at"]);
    if (Number.isFinite(previous) && sameDay(previous, at)) return;
    const heading = document.createElement("div");
    heading.className = "day";
    heading.dataset["at"] = String(at);
    heading.setAttribute("role", "separator");
    const label = document.createElement("span");
    label.className = "day-label";
    heading.appendChild(label);
    this.#transcript.appendChild(heading);
  }

  /**
   * Headings are worded when drawn and again whenever anything is added, never stored, so a chat
   * that crossed midnight, or one opened the next day, says "Yesterday" instead of "Today".
   */
  #refreshDays(): void {
    const now = Date.now();
    for (const heading of this.#transcript.querySelectorAll<HTMLElement>(".day")) {
      const at = Number(heading.dataset["at"]);
      const label = heading.querySelector<HTMLElement>(".day-label");
      if (label && Number.isFinite(at)) label.textContent = dayLabel(at, now);
    }
  }

  appendDelta(delta: string): void {
    // The first token is the proof that something is happening, so it replaces
    // the placeholder rather than appearing underneath it.
    this.hideThinking();
    if (!this.#streaming) this.#streaming = this.addMessage("assistant", "");
    // Accumulated separately from the rendered DOM: once ** is rendered into a
    // <strong>, the markers are gone from .textContent, so reading THAT back
    // and appending to it would lose the syntax needed to close the next span.
    const raw = (this.#streaming.dataset["raw"] ?? "") + delta;
    this.#streaming.dataset["raw"] = raw;
    renderRich(this.#streaming, raw);
    this.#scroll();
  }

  /**
   * Puts the saved answer in place of whatever arrived before the connection
   * dropped. docs/28 section 7b.
   *
   * 🔴 REPLACES THIS TURN'S PARTIAL BUBBLE, NEVER AN EARLIER ANSWER. Only the
   *    last row is considered, and only if it is the assistant's: if the drop
   *    came before the first word, the last row is the question and the answer
   *    is added below it instead.
   */
  settleAnswer(text: string): void {
    this.hideThinking();
    this.#streaming = null;
    const rows = this.#transcript.querySelectorAll<HTMLElement>(".row");
    const last = rows[rows.length - 1];
    const bubble = last?.dataset["role"] === "assistant" ? last.querySelector<HTMLElement>(".msg") : null;
    if (bubble && text) {
      bubble.dataset["raw"] = text;
      renderRich(bubble, text);
    } else if (text) {
      this.addMessage("assistant", text);
    }
    this.#live.textContent = text;
    this.#scroll();
  }

  endStream(): void {
    if (this.#streaming) this.#live.textContent = this.#streaming.textContent ?? "";
    this.#streaming = null;
  }

  /**
   * 🔴 THE GAP BEFORE THE FIRST TOKEN IS WHERE PEOPLE DECIDE WE ARE BROKEN.
   *
   *    A turn that calls a tool emits no text at all until the tool has run and
   *    the model has read the result: several seconds of an empty panel with a
   *    disabled input. The answer then arrived all at once, which read as "it
   *    does not stream", when in truth there had been nothing yet to stream.
   *
   *    Shown the instant Send is pressed, relabelled as the turn moves through
   *    its phases, and removed by the first token.
   */
  showThinking(label = "Thinking"): void {
    this.#q<HTMLElement>(".empty")?.remove();
    this.#setMood("thinking");

    if (!this.#thinking) {
      const row = document.createElement("div");
      row.className = "row";
      row.dataset["role"] = "assistant";
      row.appendChild(this.#avatar(true));

      const bubble = document.createElement("div");
      bubble.className = "thinking";
      bubble.setAttribute("role", "status");

      const text = document.createElement("span");
      text.className = "thinking-label";
      const dots = document.createElement("span");
      dots.className = "dots";
      dots.setAttribute("aria-hidden", "true");
      dots.append(
        document.createElement("i"),
        document.createElement("i"),
        document.createElement("i"),
      );
      bubble.append(text, dots);
      row.appendChild(bubble);
      this.#thinking = row;
    }

    this.setThinkingLabel(label);
    this.#transcript.appendChild(this.#thinking);
    this.#scroll();
  }

  /** Renames the placeholder in place, so the phases do not flicker as separate rows. */
  setThinkingLabel(label: string): void {
    const el = this.#thinking?.querySelector<HTMLElement>(".thinking-label");
    if (!el || el.textContent === label) return;
    el.textContent = label;
    // A sighted user watches the label change; everyone else hears it.
    this.#live.textContent = label;
  }

  hideThinking(): void {
    this.#thinking?.remove();
    this.#thinking = null;
    // 🔴 ONLY WHEN IT WAS THINKING. appendDelta calls this for every chunk of the answer, and the
    //    second chunk used to put the happy face of the first straight back to idle.
    if (this.#mood !== "thinking") return;
    // The answer is starting, so this is the moment to show how the actions went, for a moment.
    this.#setMood(this.#turnMood ?? "idle", this.#turnMood ? 2500 : 0);
    this.#turnMood = null;
  }

  /**
   * The moving blobs' expression (blob.ts). `hold` puts it back to idle after that long: a face
   * is a state, so a burst has to be ended by someone, and nothing else would.
   */
  #setMood(mood: Mood, hold = 0): void {
    if (this.#moodTimer) clearTimeout(this.#moodTimer);
    this.#moodTimer = hold ? setTimeout(() => this.#setMood("idle"), hold) : null;
    if (mood === this.#mood || !this.#avatarBlob) {
      this.#mood = mood;
      return;
    }
    this.#mood = mood;
    setMood(mood);
  }

  /**
   * Marks the last question as saved on the server.
   *
   * Editing has to unwind server-side history, and it can only count turns that
   * are actually there. A question whose turn died before it was written, a
   * blocked origin, a dropped connection, was never persisted, so counting it
   * would unwind one turn too many and silently delete a real exchange.
   */
  /**
   * Offers a thumb on the answer that was just written down.
   *
   * 🔴 ON THE LAST ASSISTANT ROW, AND ONLY WHEN THE SERVER GAVE US ITS ID. A
   *    written starter answer never reaches the database, so it gets no thumb.
   *    Offering one there would produce a control that silently does nothing,
   *    which is worse than not offering it at all.
   *
   * 🔴 A SIBLING OF THE BUBBLE, NEVER A CHILD. `.msg` holds the message text and
   *    nothing else, because snapshot() and the live region both read its
   *    textContent. Putting "Was this helpful?" inside it would append that to
   *    every answer a screen reader announces and every transcript we restore.
   *    Same reasoning as the avatar directly above.
   *
   * 🔴 A CLICK OPENS THE BOX. IT DOES NOT SEND. docs/29 section 5, and it is
   *    the single most consequential line in that document. Both ratings ask
   *    for a comment, so the owner-facing view always has context rather than a
   *    column of bare thumbs nobody can act on. The comment stays optional, so
   *    the cost of rating is still one click plus one Submit.
   *
   *    This is a deliberate reversal of the first version, which sent the
   *    rating the instant a thumb was pressed and only then asked why. That
   *    version could not offer a Cancel, because there was nothing left to
   *    cancel, so a misclick was permanent and a thumbs-up could never be
   *    accompanied by a reason.
   *
   * `given` is the hydration path: a rating this visitor already left, coming
   * back from sessionStorage after a reload. docs/29 section 4.
   *
   * 🔴 THE LOCK IS ON THE MESSAGE ID, NOT ON THE BAR. It used to live in this
   *    method's own closure, which meant it protected the bar it created and
   *    nothing else: any second bar for the same answer came up with live
   *    thumbs and would have posted a second rating. That is one answer
   *    reported twice on a customer's dashboard, from a control that was
   *    supposed to be spent. `#rated` survives every re-render, every restore
   *    and every scroll, because it is keyed by the thing that is actually
   *    unique: the id the server gave the message.
   */
  offerFeedback(messageId: string, given?: { rating: "up" | "down" }): void {
    if (!messageId) return;
    if (given) this.#rated.set(messageId, given.rating);
    const rows = [...this.#transcript.querySelectorAll<HTMLElement>('.row[data-role="assistant"]')];
    const row = rows[rows.length - 1];
    if (!row || row.querySelector(".fb")) return;
    // A row with no bubble is a tool-only or thinking row. Nothing to rate.
    const msg = row.querySelector<HTMLElement>(".msg");
    if (!msg) return;

    // Carried on the bubble so snapshot() can find it without a parallel map.
    // The DOM is already the one source of truth for what is on screen.
    msg.dataset["mid"] = messageId;

    const bar = document.createElement("div");
    bar.className = "fb";
    bar.dataset["state"] = "asking";

    const ask = document.createElement("div");
    ask.className = "fb-row";

    /**
     * 🔴 "Was this helpful?" IS COMMENTED OUT, NOT DELETED. Vishnu's call: two
     *    thumbs are self-explanatory and the sentence was the widest thing in
     *    the row, which is what pushed the tool summary onto its own line
     *    above the answer. If the response rate drops noticeably, the question
     *    is the first thing to put back, so it stays here to be uncommented
     *    rather than rewritten from memory.
     *
     * const label = document.createElement("span");
     * label.className = "fb-label";
     * label.textContent = "Was this helpful?";
     */

    /**
     * 🔴 A RATED ANSWER NEVER GETS THE CONTROLS BACK. Not greyed out, not
     *    present and inert: absent. The bar renders as a record of what was
     *    said and stops being a thing you can press, which is the only version
     *    of "already rated" that cannot be pressed again.
     */
    const already = this.#rated.get(messageId);
    if (already) {
      row.appendChild(bar);
      this.#settleFeedback(bar, already, { recorded: true });
      this.#adoptToolSummary(row, bar);
      this.#scroll();
      return;
    }

    const up = this.#thumb("up");
    const down = this.#thumb("down");
    ask.append(up, down);
    bar.append(ask);
    row.appendChild(bar);
    this.#adoptToolSummary(row, bar);

    /**
     * 🔴 LOCKED BY `sent`, NEVER BY "HAS A RATING". docs/29 section 5b. A
     *    person is allowed to press up, read the prompt, decide that is not
     *    what they meant, cancel, and press down. Disabling on selection takes
     *    that away and turns a misclick into a permanent wrong number on
     *    somebody's dashboard.
     */
    let sent = false;
    let rating: "up" | "down" | null = null;

    const paint = () => {
      for (const [kind, b] of [["up", up], ["down", down]] as const) {
        b.dataset["selected"] = String(rating === kind);
        b.setAttribute("aria-pressed", String(rating === kind));
        b.disabled = sent;
      }
    };

    const close = () => {
      bar.querySelector(".fb-card")?.remove();
      rating = null;
      bar.dataset["state"] = "asking";
      paint();
    };

    const open = (kind: "up" | "down") => {
      if (sent) return;
      rating = kind;
      bar.dataset["state"] = "open";
      bar.querySelector(".fb-card")?.remove();
      const card = this.#feedbackCard(kind, {
        cancel: close,
        submit: (note) => {
          sent = true;
          // Recorded BEFORE the callback, so a throw on the way to the network
          // cannot leave a spent control still pressable.
          this.#rated.set(messageId, kind);
          this.cb.onFeedback(messageId, kind, note || undefined);
          this.#settleFeedback(bar, kind);
        },
      });
      bar.append(card);
      /**
       * 🔴 AFTER THE APPEND, NOT INSIDE THE BUILDER. focus() on a node that is
       *    not yet in the document does nothing at all, silently, and the box
       *    opens with the caret still in the composer. Caught by a test that
       *    found the composer holding focus instead. The click that opened this
       *    was deliberate; do not make them click again.
       */
      card.querySelector<HTMLTextAreaElement>(".fb-note")?.focus();
      paint();
      this.#scroll();
    };

    up.addEventListener("click", () => open("up"));
    down.addEventListener("click", () => open("down"));
    paint();

    this.#scroll();
  }

  /**
   * 🔴 AN INLINE SVG, NOT AN EMOJI. The selected state in docs/29 section 5b is
   *    a TEXT colour, and an emoji ignores one: it renders in whatever the
   *    platform's font decided, so 👍 and a green 👍 look identical on most
   *    machines and the whole state indication silently disappears. currentColor
   *    on a path is the only version of this that actually works everywhere.
   */
  #thumb(kind: "up" | "down"): HTMLButtonElement {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "fb-thumb";
    b.dataset["kind"] = kind;
    // docs/29 section 5b, exactly. "Good answer" rather than "thumbs up",
    // because a screen reader user needs what it MEANS, not what it looks like.
    const label = kind === "up" ? "Good answer" : "Bad answer";
    b.setAttribute("aria-label", label);
    b.setAttribute("aria-pressed", "false");
    b.title = label;
    b.append(this.#thumbIcon(kind));
    return b;
  }

  /**
   * The glyph, built once so the pressable thumb and the settled mark cannot
   * drift into two different shapes for the same rating.
   */
  #thumbIcon(kind: "up" | "down"): HTMLSpanElement {
    const host = document.createElement("span");
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("width", "14");
    svg.setAttribute("height", "14");
    svg.setAttribute("fill", "currentColor");
    svg.setAttribute("aria-hidden", "true");
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute(
      "d",
      kind === "up"
        ? "M2 10h4v11H2zM21.6 9.4A2 2 0 0 0 20 8.7h-5.1l.7-3.4a2.4 2.4 0 0 0-.6-2.1 2.3 2.3 0 0 0-3.4.2L8 8.4V21h9.6a2 2 0 0 0 2-1.6l1.5-7.7a2 2 0 0 0-.5-1.7z"
        : "M22 14h-4V3h4zM2.4 14.6a2 2 0 0 0 1.6.7h5.1l-.7 3.4a2.4 2.4 0 0 0 .6 2.1 2.3 2.3 0 0 0 3.4-.2l3.6-5V3H6.4a2 2 0 0 0-2 1.6l-1.5 7.7a2 2 0 0 0 .5 1.7z",
    );
    svg.append(path);
    host.append(svg);
    return host;
  }

  /**
   * The box that opens under a thumb.
   *
   * 🔴 THE COPY DIFFERS BY RATING AND THE `(optional)` IS LOAD-BEARING.
   *    docs/29 section 5b. Without that word, somebody with nothing to add
   *    abandons the box and you lose the rating you had already earned. The
   *    placeholder offers example adjectives on purpose: agreeing with three
   *    words is faster than filling a blank field.
   */
  #feedbackCard(
    kind: "up" | "down",
    on: { cancel: () => void; submit: (note: string) => void },
  ): HTMLElement {
    const card = document.createElement("div");
    card.className = "fb-card";
    // The card border repeats the rating, so an open box always shows which
    // way it is about to be sent. docs/29 section 5b.
    card.dataset["rating"] = kind;

    const prompt = document.createElement("label");
    prompt.className = "fb-card-label";
    prompt.textContent =
      kind === "up" ? "What did you like? (optional)" : "What went wrong? (optional)";

    const note = document.createElement("textarea");
    note.className = "fb-note";
    note.rows = 2;
    note.placeholder = kind === "up" ? "Accurate, helpful, fast..." : "Incorrect, unhelpful, confusing...";
    note.setAttribute("aria-label", prompt.textContent);
    /**
     * 🔴 THE SAME 200 THE SERVER TRUNCATES AT, so a long note is stopped while
     *    they are still typing rather than silently shortened after they send.
     *    Short on purpose: a box that accepts an essay is a box that asks for
     *    one, and nobody writes an essay about a chat reply.
     */
    note.maxLength = 200;
    prompt.append(note);

    const actions = document.createElement("div");
    actions.className = "fb-actions";

    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.className = "fb-cancel";
    cancel.textContent = "Cancel";
    cancel.addEventListener("click", on.cancel);

    const submit = document.createElement("button");
    submit.type = "button";
    submit.className = "fb-submit";
    submit.textContent = "Submit";
    submit.addEventListener("click", () => on.submit(note.value.trim()));

    // Ghost on the left, primary on the right. docs/29 section 5b.
    actions.append(cancel, submit);
    card.append(prompt, actions);

    note.addEventListener("keydown", (e) => {
      // Matches the composer this visitor has already been using: Enter sends,
      // Shift+Enter is a newline. Two different rules for Enter inside one
      // panel is the kind of thing people never learn, they only get wrong.
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        on.submit(note.value.trim());
      }
      if (e.key === "Escape") {
        e.preventDefault();
        on.cancel();
      }
    });

    // Focused by the caller, once this is actually in the document.
    return card;
  }

  /**
   * Replaces the bar with one quiet line. Never a toast, never a dialog.
   *
   * 🔴 THE THUMBS-UP LINE BELONGS TO THE SITE OWNER. docs/29 section 8.3.
   *    TradePilot spends this moment on a Chrome Web Store review, which is
   *    right for a product whose users are its own. Ours are not: it is the one
   *    moment on a customer's site where a visitor has said, unprompted, that
   *    the thing worked, and only that customer knows whether that is worth a
   *    testimonial request, a discount code, or nothing at all. The default is
   *    nothing at all, because a default that asked for something would be
   *    putting words in their mouth on their own page.
   */
  #settleFeedback(bar: HTMLElement, kind: "up" | "down", opts?: { recorded: boolean }): void {
    bar.dataset["state"] = "done";
    bar.dataset["rating"] = kind;

    /**
     * 🔴 THE SUMMARY IS RESCUED BEFORE THE BAR IS EMPTIED, AND IT USED TO BE
     *    THROWN AWAY HERE. `replaceChildren()` clears the thumbs, and the
     *    adopted "Show details" button lives among them, so rating an answer
     *    silently removed the only way back to the record of what ran. The
     *    audit trail disappearing the moment somebody says the answer was
     *    wrong is the exact opposite of what this product sells.
     */
    const summary = bar.querySelector<HTMLElement>(".tool-summary");
    // The list lives in the bar too, so it needs rescuing for the same reason.
    const group = bar.querySelector<HTMLElement>(".tools");
    bar.replaceChildren();

    const line = document.createElement("div");
    line.className = "fb-row";
    if (summary) line.append(summary);

    /**
     * 🔴 THE MARK IS THE ANSWER TO "DID THAT GO THROUGH". A line of grey text
     *    saying "thanks" reads the same whichever thumb was pressed, so
     *    somebody who meant to complain has no way to tell that a complaint is
     *    what was recorded. Red for a thumbs-down, green for a thumbs-up, the
     *    same two colours the live thumbs use, so the settled state is
     *    recognisably the thing that was just clicked rather than a new
     *    element that replaced it.
     *
     *    It is a SPAN, not a button. See the note in offerFeedback: the only
     *    version of "already rated" that cannot be pressed again is one with
     *    nothing to press.
     */
    const mark = this.#thumbIcon(kind);
    mark.className = "fb-mark";
    mark.dataset["kind"] = kind;

    const said = document.createElement("span");
    said.className = "fb-label";
    said.textContent = opts?.recorded
      ? kind === "up"
        ? "You marked this helpful."
        : "You marked this as wrong."
      : kind === "up"
        ? this.#feedbackThanks || "Thanks, glad it helped."
        : "Thanks for flagging this, we will look at it.";

    line.append(mark, said);
    bar.append(line);

    /**
     * 🔴 THE URL IS CHECKED HERE TOO, THOUGH THREE OTHER PLACES ALREADY DID.
     *    It arrives from /v1/config, which serves what the owner saved, and it
     *    becomes an href on the customer's own origin where `javascript:` is
     *    script execution on their domain. Migration 0020 constrains it, the
     *    admin route refuses it, and this refuses it again, because the one
     *    that has to hold is whichever one a future hand forgets to write.
     */
    if (kind === "up" && this.#feedbackThanksUrl) {
      const href = safeUrl(this.#feedbackThanksUrl, location.href);
      if (href) {
        const a = document.createElement("a");
        a.className = "fb-link";
        a.href = href;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        a.textContent = "Tell them";
        line.append(a);
      }
    }

    // Last, so the record still sits under the row that opens it.
    if (group) bar.append(group);
  }

  /** What the owner wants said after a thumbs up, from /v1/config. */
  setFeedbackThanks(text: string | null | undefined, url: string | null | undefined): void {
    this.#feedbackThanks = (text ?? "").trim();
    this.#feedbackThanksUrl = (url ?? "").trim();
  }

  #feedbackThanks = "";
  #feedbackThanksUrl = "";

  /**
   * Every answer this visitor has already rated, and how.
   *
   * 🔴 KEYED BY THE SERVER'S MESSAGE ID, WHICH IS THE ONLY THING THAT SURVIVES
   *    A RE-RENDER. Held here rather than in offerFeedback's closure, because
   *    a closure protects the one bar it made and nothing else, and a second
   *    bar for the same answer would have come up fully armed.
   *
   *    Cleared by newChat and nowhere else: a new conversation is genuinely a
   *    different set of messages, and letting this grow across a long thread
   *    costs one short string per rating.
   */
  readonly #rated = new Map<string, "up" | "down">();

  /**
   * Renders a tool result as things you can look at.
   *
   * 🔴 EVERY STRING GOES IN WITH textContent AND THE MARKUP IS BUILT NODE BY
   *    NODE. This is a customer's own data, on a customer's own page, and some
   *    of it started life as something a user of theirs typed. One innerHTML
   *    here is an XSS hole on their origin, which is the single worst thing
   *    this widget could do to them.
   *
   * 🔴 IT SITS BESIDE THE ANSWER, NOT INSTEAD OF IT. The model's text still
   *    describes the whole result, including the rows past the cap. Cards make
   *    the first few concrete; they are not the answer.
   */
  addCards(cards: readonly Card[]): void {
    if (cards.length === 0) return;
    this.#q<HTMLElement>(".empty")?.remove();

    const row = document.createElement("div");
    row.className = "row";
    row.dataset["role"] = "assistant";

    const wrap = document.createElement("div");
    wrap.className = "cards";

    for (const c of cards) {
      // An anchor only when there is somewhere safe to go: safeUrl has already
      // refused anything that is not https, or http on localhost.
      const el = document.createElement(c.url ? "a" : "div");
      el.className = "card";
      if (c.url && el instanceof HTMLAnchorElement) {
        el.href = c.url;
        el.target = "_blank";
        // noopener is not optional on a target=_blank we did not author: without
        // it the opened page gets a handle on the customer's window.
        el.rel = "noopener noreferrer";
      }

      if (c.image) {
        const img = document.createElement("img");
        img.className = "card-img";
        img.src = c.image;
        img.alt = "";
        img.loading = "lazy";
        img.decoding = "async";
        // 🔴 The customer's page URL must not leak to whoever hosts the image.
        img.referrerPolicy = "no-referrer";
        // A broken image hides rather than leaving a torn-icon in a product card.
        img.addEventListener("error", () => img.remove());
        el.appendChild(img);
      }

      const body = document.createElement("div");
      body.className = "card-body";

      const title = document.createElement("span");
      title.className = "card-title";
      title.textContent = c.title;
      body.appendChild(title);

      if (c.subtitle) {
        const sub = document.createElement("span");
        sub.className = "card-sub";
        sub.textContent = c.subtitle;
        body.appendChild(sub);
      }

      if (c.price || c.rating) {
        const meta = document.createElement("span");
        meta.className = "card-meta";
        if (c.price) {
          const p = document.createElement("strong");
          p.textContent = c.price;
          meta.appendChild(p);
        }
        if (c.rating) {
          const r = document.createElement("span");
          r.className = "card-rating";
          r.textContent = `\u2605 ${c.rating}`;
          meta.appendChild(r);
        }
        body.appendChild(meta);
      }

      el.appendChild(body);
      wrap.appendChild(el);
    }

    row.appendChild(wrap);
    this.#transcript.appendChild(row);
    if (this.#thinking) this.#transcript.appendChild(this.#thinking);
    this.#scroll();
  }

  commitTurn(): void {
    const rows = this.#userRows();
    const last = rows[rows.length - 1];
    if (last) last.dataset["committed"] = "true";
  }

  /**
   * One collapsible group per turn, holding every tool that turn called.
   *
   * 🔴 CHIPS USED TO GO STRAIGHT INTO THE TRANSCRIPT AS LOOSE SIBLINGS, and
   *    that read as a malfunction the first time a real answer needed several
   *    calls. Vishnu asked one question on mirchi35, saw four identical "List
   *    businesses" rows stacked above the answer, and reasonably asked whether
   *    the same tool was firing four times by mistake. It was not: the model
   *    was paging through results, pages 1 to 4, which is exactly what it
   *    should do to count everything. But nothing on screen said so, and four
   *    copies of one label is indistinguishable from a stutter.
   *
   *    The steps are worth having and they are not worth reading first. So
   *    while the turn runs only the CURRENT step shows, as a live line, and
   *    when the answer lands the whole group folds into one summary that says
   *    how many tools ran. Anybody who wants the list opens it.
   *
   *    Same shape TradePilot's side panel arrived at, and for the same reason
   *    recorded there: auto-expanding the details on multi-tool answers was
   *    tried and pulled the same day, because it puts a panel of scaffolding
   *    under every answer and pushes the answer itself off screen.
   */
  #toolGroup: HTMLElement | null = null;
  #toolCount = 0;
  /**
   * Which "Show details" button belongs to which tool group.
   *
   * 🔴 A MAP, BECAUSE THE BUTTON DOES NOT LIVE IN THE GROUP ANY MORE. Once the
   *    turn is answered, #adoptToolSummary moves it into the feedback bar, so
   *    `group.querySelector(".tool-summary")` finds nothing and every caller
   *    that relied on it silently no-ops. That is how the label got stuck
   *    reading "Show details" on an already-open list: the list expanded, the
   *    button was never told.
   *
   *    Weak, so a group removed by an edit or a rewind takes its entry with it
   *    rather than pinning a detached button in memory for the life of the
   *    page.
   */
  #summaries = new WeakMap<HTMLElement, HTMLElement>();
  /**
   * When this turn's first tool started, for the elapsed time on the summary.
   *
   * 🔴 WALL CLOCK FROM THE FIRST CHIP, NOT THE SUM OF THE CALLS. Tools can
   *    overlap, and a turn that ran three things at once in two seconds would
   *    report six if the per-call times were added up. What somebody wants to
   *    know is how long they waited, which is the wall clock.
   */
  #toolStart = 0;

  addToolChip(callId: string, name: string, shownLabel?: string): void {
    // The chip is itself a progress indicator, and a more specific one. The row goes but the
    // face stays on thinking, since the action is still work: hideThinking would show the
    // previous action's outcome here, before the answer, and use it up.
    this.#thinking?.remove();
    this.#thinking = null;

    if (!this.#toolGroup) {
      const group = document.createElement("div");
      group.className = "tools";
      // "running" hides all but the last chip; see the CSS. Starts closed so
      // that finishing the turn never has to decide whether to close it.
      group.dataset["state"] = "running";
      group.dataset["open"] = "false";
      const list = document.createElement("div");
      list.className = "tool-list";
      // The heading only appears when the list is expanded; see the CSS. It
      // exists so the opened panel reads as a record of what ran rather than
      // as loose text that appeared under a link.
      const heading = document.createElement("p");
      heading.className = "tool-list-head";
      heading.textContent = "Tools used";
      list.append(heading);
      group.append(this.#toolSummary(group), list);
      this.#transcript.appendChild(group);
      this.#toolGroup = group;
      this.#toolCount = 0;
      this.#toolStart = Date.now();
    }

    const el = document.createElement("div");
    el.className = "tool";
    el.dataset["call"] = callId;
    el.dataset["status"] = "running";

    el.append(this.#toolIcon("running"));
    const label = document.createElement("span");
    label.className = "tool-label";
    // The customer's own sentence when the server sent one; the action's name made
    // readable only for a widget talking to a server that does not send labels.
    label.textContent = shownLabel ?? humanise(name);
    el.append(label);

    this.#toolGroup.querySelector(".tool-list")?.appendChild(el);
    this.#toolCount += 1;
    this.#scroll();
  }

  /**
   * The one row that stays visible once the turn is done.
   *
   * 🔴 THE GROUP IS PASSED IN AND HELD, NOT FOUND WITH `closest()`, AND THE
   *    DIFFERENCE IS THE WHOLE BUTTON WORKING. It used to walk up the tree for
   *    its `.tools` ancestor, which was true right up until #adoptToolSummary
   *    moved the button out of that ancestor and into the feedback bar. After
   *    the move `closest(".tools")` returns null, the handler returns on its
   *    guard clause, and "Show details" is a control that highlights on hover
   *    and does absolutely nothing. Shipped that way in 632d921.
   *
   *    Every DOM test passed, because they all clicked the button while it was
   *    still inside the group: the tests reached for it via `.tools
   *    .tool-summary`, which is the selector that stops matching. So the test
   *    added with this fix clicks it where a person actually finds it, from
   *    the feedback row, after the move has happened.
   */
  /** The owner's view of exactly what an action will send, collapsed, on a test site only. */
  #debugDetails(debug: DebugInfo): HTMLElement {
    const box = document.createElement("details");
    box.className = "confirm-debug";
    const summary = document.createElement("summary");
    summary.textContent = "Developer details";
    const pre = document.createElement("pre");
    // textContent, never markup: these are values from somebody else's system.
    pre.textContent = JSON.stringify({ action: debug.action, arguments: debug.args }, null, 2);
    box.append(summary, pre);
    return box;
  }

  #toolSummary(group: HTMLElement): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "tool-summary";
    btn.setAttribute("aria-expanded", "false");
    this.#summaries.set(group, btn);
    btn.addEventListener("click", () => {
      const open = group.dataset["open"] === "true";
      group.dataset["open"] = open ? "false" : "true";
      btn.setAttribute("aria-expanded", open ? "false" : "true");
      this.#syncToolSummary(group);
    });
    return btn;
  }

  /**
   * Moves this turn's "Show details" button into the feedback row beside the
   * thumbs.
   *
   * 🔴 THE SUMMARY WAS A LINE OF ITS OWN ABOVE THE ANSWER, which is the wrong
   *    place twice over: it separated the question from the reply, and it
   *    spent a whole row on a control almost nobody presses. Beside the thumbs
   *    it sits in a row that already exists, under the answer it describes.
   *
   * 🔴 THE LIST COMES WITH IT, AND LEAVING IT BEHIND WAS WRONG. The first
   *    version moved only the button, on the reasoning that opening it would
   *    then expand the record "in place". In place turned out to mean several
   *    rows further up, above the question and above the answer, because that
   *    is where the group was built while the turn was still running. So
   *    pressing Show details made a list appear somewhere off the top of the
   *    panel, with the button that opened it still sitting at the bottom.
   *
   *    The whole group moves into the bar now, under the row that opens it.
   *    While the turn RUNS it stays where it is, as a live line saying what is
   *    happening; it only moves once there is an answer for it to sit beneath.
   *
   * 🔴 INTO `.fb-row`, NOT INTO `.fb`, AND THE DIFFERENCE IS A WHOLE LINE.
   *    `.fb` is a flex COLUMN, because the comment card has to open underneath
   *    the thumbs. Appending the button there stacked it below them and made
   *    the `margin-left: auto` in the stylesheet a no-op, which is exactly the
   *    two-line layout this method was written to remove. The row is the only
   *    child of the bar that lays its children out side by side.
   *
   * 🔴 FIRST IN THE ROW, AHEAD OF THE THUMBS. Vishnu's call, and the reading
   *    order is the argument: the button describes what the assistant just
   *    did, the thumbs ask what you thought of it. Fact before verdict.
   */
  #adoptToolSummary(row: HTMLElement, bar: HTMLElement): void {
    // The group for this turn is the last one before this row in the
    // transcript. Reading the DOM rather than holding a reference keeps this
    // correct through an edit or a rewind, which rewrite rows underneath it.
    //
    // 🔴 ONLY A GROUP NOT ALREADY ADOPTED. An adopted group sits inside an earlier answer's
    //    feedback bar. Without this, an answer that used no tool took the PREVIOUS answer's
    //    group, so "Show details · 1 tool · 3.7s" jumped to the newest answer each turn and
    //    claimed a tool had run for words that had none (seen 2026-10-07).
    const groups = [...this.#transcript.querySelectorAll<HTMLElement>(".tools")].filter((g) => !g.closest(".fb"));
    const group = groups[groups.length - 1];
    if (!group) return;
    const summary = this.#summaries.get(group);
    if (!summary) return;

    (bar.querySelector<HTMLElement>(".fb-row") ?? bar).prepend(summary);
    /**
     * After the row, so the list opens directly underneath the button that
     * opens it. `.fb` is a column, so appending is all the ordering needed.
     *
     * Moving the group does not disturb the button: it holds its group by
     * closure and looks it up through #summaries, neither of which cares where
     * in the document either element sits. That is the same property that
     * makes this safe, and the reason `closest()` had to go.
     */
    bar.append(group);
  }

  #syncToolSummary(group: HTMLElement): void {
    // Never group.querySelector: see #summaries. The button may have moved.
    const btn = this.#summaries.get(group);
    if (!btn) return;
    const n = group.querySelectorAll(".tool").length;
    /**
     * 🔴 CANCELLED IS NOT FAILED, AND ROLLING THEM TOGETHER LIBELS THE USER.
     *    "1 failed" on a turn where somebody read the card and pressed Cancel
     *    reports their decision as a malfunction. One is the product working
     *    exactly as designed; the other is something to look into.
     */
    const failed = group.querySelectorAll('.tool[data-status="error"]').length;
    const cancelled = group.querySelectorAll('.tool[data-status="denied"]').length;
    const unconfirmed = group.querySelectorAll('.tool[data-status="unconfirmed"]').length;
    const changed = group.querySelectorAll('.tool[data-status="changed"]').length;
    const open = group.dataset["open"] === "true";
    /**
     * 🔴 SECONDS, ONE DECIMAL, AND ONLY ONCE THE TURN IS DONE. A timer that
     *    ticks while you wait is a second thing moving on a screen that is
     *    already streaming text, and the number is not actionable until it has
     *    stopped. What it answers afterwards is the question people actually
     *    ask about an agent: what was it doing for all that time.
     */
    /**
     * 🔴 A TENTH OF A SECOND IS THE FLOOR, because "0.0s" is not information.
     *    A cached or instant turn would otherwise print a duration of zero,
     *    which reads as a bug rather than as speed. Below the floor the time
     *    is simply not claimed.
     */
    const secs = Number(group.dataset["elapsed"] ?? 0);
    const took = secs >= 0.1 ? ` · ${duration(secs)}` : "";
    btn.textContent = `${open ? "Hide" : "Show"} details · ${n} tool${n === 1 ? "" : "s"}${took}${
      failed > 0 ? ` · ${failed} failed` : ""
    }${cancelled > 0 ? ` · ${cancelled} cancelled` : ""}${
      unconfirmed > 0 ? ` · ${unconfirmed} not confirmed` : ""
    }${changed > 0 ? ` · ${changed} changed` : ""}`;
  }

  /**
   * The turn is over, however it ended. Folds this turn's tools away.
   *
   * Called from the send loop's `finally`, not from `endStream`: endStream
   * fires on every `tool_start` too, so collapsing there would close the group
   * between calls and reopen it on the next one.
   */
  /**
   * The transcript as data, in order, for sessionStorage.
   *
   * Reads the DOM rather than keeping a parallel array, so there is exactly one
   * source of truth for what is on screen. An edit rewrites rows in place and a
   * rewind removes them; a shadow copy would have to be taught about both, and
   * the failure mode of getting that wrong is restoring a conversation that
   * never happened.
   */
  snapshot(): SavedEntry[] {
    const out: SavedEntry[] = [];
    for (const node of Array.from(this.#transcript.children)) {
      const el = node as HTMLElement;
      if (el.classList.contains("row")) {
        /**
         * 🔴 A TURN'S TOOLS ARE NOT ALWAYS A SIBLING OF ITS ANSWER. The moment an answer
         *    is saved and can be rated, #adoptToolSummary MOVES the whole group, its
         *    "Show details" button and its list, into the feedback bar inside that
         *    answer's row. This loop only looked at the transcript's direct children, so
         *    from that moment the group was invisible to it: every turn that ran a tool
         *    saved its messages and nothing else, and a refresh brought back the
         *    conversation without the tools used or the details (Vishnu, 2026-10-08).
         *    Pushed BEFORE the message because restore re-adopts the last group that
         *    precedes a row, which is the order they had when it first happened.
         */
        const adopted = el.querySelector<HTMLElement>(".tools");
        if (adopted) {
          const saved = this.#snapshotGroup(adopted);
          if (saved) out.push(saved);
        }
        const msg = el.querySelector<HTMLElement>(".msg");
        const role = msg?.dataset["role"];
        if (!msg || (role !== "user" && role !== "assistant")) continue;
        // The raw markdown for an assistant, so restoring re-renders rather
        // than storing markup. textContent for a user, which is all there is.
        const text = role === "assistant" ? (msg.dataset["raw"] ?? msg.textContent ?? "") : (msg.textContent ?? "");
        if (!text) continue;
        /**
         * 🔴 THE RATING SURVIVES THE RELOAD, AND SO DOES THE ID THAT MAKES IT
         *    MEAN ANYTHING. docs/29 section 4: somebody who rated an answer
         *    yesterday and comes back to empty buttons rates it again, learns
         *    their input went nowhere, and does not bother a third time.
         *
         * 🔴 A SUBMITTED RATING ONLY, NEVER AN OPEN BOX. docs/29 section 5b
         *    calls a half-open comment box restored from storage a thing that
         *    looks like a bug, and TradePilot has to strip a `feedbackOpen`
         *    flag on save to avoid it. Here there is no flag to strip: this
         *    reads the settled state off the DOM, so the open box is
         *    unrepresentable in storage rather than merely cleared.
         *
         * 🔴 AND NO COPY OF THE COMMENT. It is already on the server, attached
         *    to the message it was about and expiring with it. A second copy in
         *    the visitor's own browser would be end-user text sitting on the
         *    customer's origin for no purpose the panel can serve.
         */
        const mid = msg.dataset["mid"];
        const settled = el.querySelector<HTMLElement>('.fb[data-state="done"]');
        const fb = settled?.dataset["rating"];
        const at = Number(el.dataset["at"]);
        out.push({
          k: "m",
          role,
          text,
          ...(mid ? { id: mid } : {}),
          ...(fb === "up" || fb === "down" ? { fb } : {}),
          ...(isPlausibleTime(at) ? { at } : {}),
        });
      } else if (el.classList.contains("tools")) {
        const saved = this.#snapshotGroup(el);
        if (saved) out.push(saved);
      }
    }
    return out;
  }

  /** One tool group as data: what ran, how long it took, and what the visitor approved. */
  #snapshotGroup(group: HTMLElement): SavedEntry | null {
    const chips = group.querySelectorAll<HTMLElement>(".tool");
    if (chips.length === 0) return null;
    const failed = group.querySelectorAll('.tool[data-status="error"], .tool[data-status="denied"]').length;
    const elapsed = Number(group.dataset["elapsed"]);
    const cards: SavedCard[] = [];
    // Only an approval that was ANSWERED. One nobody answered has no outcome to record.
    for (const card of group.querySelectorAll<HTMLElement>('.confirm[data-resolved="true"]')) {
      const saved = this.#snapshotCard(card);
      if (saved) cards.push(saved);
    }
    return {
      k: "t",
      n: chips.length,
      failed,
      // Label and outcome together, so restore cannot pair them wrongly.
      calls: Array.from(chips, (c) => ({
        name: c.querySelector(".tool-label")?.textContent ?? "",
        status: c.dataset["status"] ?? "ok",
      })),
      // The same floor the summary uses: under a tenth of a second it prints nothing,
      // so there is nothing to restore.
      ...(Number.isFinite(elapsed) && elapsed >= 0.1 ? { elapsed } : {}),
      ...(cards.length > 0 ? { cards } : {}),
    };
  }

  /**
   * One resolved approval, read back off the DOM as the visitor saw it.
   *
   * 🔴 THE ROWS ARE WHAT IS ON THE CARD, NEVER WHAT IS BEHIND IT. Read from the screen
   *    rather than kept in a parallel structure, so what is saved can only ever be what
   *    the visitor was shown. Since phase 5 that is rows built for a person, which is why
   *    keeping it is safe where keeping the arguments would not have been.
   */
  #snapshotCard(card: HTMLElement): SavedCard | null {
    const title = card.querySelector(".confirm-title")?.textContent?.trim();
    const outcome = card.querySelector<HTMLElement>(".confirm-outcome");
    const decision = outcome?.dataset["decision"];
    if (!title || !outcome || (decision !== "allow" && decision !== "deny")) return null;
    // The card's own list, or the multi-step review when its fields were stepped. Plain
    // child iteration rather than selectors with :scope or :not(), which are the first
    // things a lighter DOM implementation gets wrong.
    const lists = Array.from(card.querySelectorAll("dl.confirm-args"));
    const list = lists.find((l) => !l.classList.contains("vstep-review")) ?? lists[0];
    const rows = Array.from(list?.children ?? [], (r) => ({
      label: r.querySelector("dt")?.textContent ?? "",
      value: r.querySelector("dd")?.textContent ?? "",
    })).filter((r) => r.label || r.value);
    const warning = card.querySelector(".confirm-warning")?.textContent?.trim();
    return {
      title,
      rows,
      outcome: outcome.textContent?.trim() ?? "",
      decision,
      ...(warning ? { warning } : {}),
    };
  }

  /** A resolved approval put back on screen: the same markup a live one ends up with. */
  #resolvedCard(saved: SavedCard): HTMLElement {
    const card = document.createElement("div");
    card.className = "confirm";
    card.dataset["resolved"] = "true";
    card.setAttribute("role", "group");
    card.setAttribute("aria-label", "Confirm action");

    const head = document.createElement("div");
    head.className = "confirm-head";
    const eyebrow = document.createElement("p");
    eyebrow.className = "confirm-eyebrow";
    eyebrow.textContent = "Needs your approval";
    const title = document.createElement("p");
    title.className = "confirm-title";
    title.textContent = saved.title;
    head.append(eyebrow, title);
    card.appendChild(head);

    if (saved.warning) {
      const warn = document.createElement("p");
      warn.className = "confirm-warning";
      warn.textContent = saved.warning;
      card.appendChild(warn);
    }
    if (saved.rows.length > 0) {
      const dl = document.createElement("dl");
      dl.className = "confirm-args";
      for (const r of saved.rows) {
        const row = document.createElement("div");
        const dt = document.createElement("dt");
        dt.textContent = r.label;
        const dd = document.createElement("dd");
        dd.textContent = r.value;
        row.append(dt, dd);
        dl.appendChild(row);
      }
      card.appendChild(dl);
    }
    const outcome = document.createElement("p");
    outcome.className = "confirm-outcome";
    outcome.dataset["decision"] = saved.decision;
    outcome.textContent = saved.outcome;
    card.appendChild(outcome);
    return card;
  }

  /**
   * Puts a saved conversation back on screen.
   *
   * 🔴 Tool groups come back COLLAPSED and as a count, not as individual
   *    chips. Each chip's per-call status is live state about a call that
   *    finished before the reload, and there is nothing left to update it
   *    from. A summary reading "4 tools" is the true and complete thing we
   *    still know.
   */
  restore(entries: readonly SavedEntry[]): void {
    if (entries.length === 0) return;
    for (const e of entries) {
      if (e.k === "m") {
        // A transcript saved before times existed has none, and shows none, rather than invent one.
        this.addMessage(e.role, e.text, e.at ?? null);
        // Puts the thumbs back exactly as they were left, filled in or blank.
        // Nothing is re-sent: `offerFeedback` with a rating renders the settled
        // line and never calls back. docs/29 section 4.
        if (e.role === "assistant" && e.id) {
          this.offerFeedback(e.id, e.fb ? { rating: e.fb } : undefined);
        }
      } else {
        const group = document.createElement("div");
        group.className = "tools";
        group.dataset["state"] = "done";
        group.dataset["open"] = "false";
        const summary = this.#toolSummary(group);
        const list = document.createElement("div");
        list.className = "tool-list";
        // The same heading a live group has, so an opened one reads as the same thing.
        const heading = document.createElement("p");
        heading.className = "tool-list-head";
        heading.textContent = "Tools used";
        list.append(heading);
        if (e.elapsed) group.dataset["elapsed"] = String(e.elapsed);
        // Rebuilt so the count and the "N failed" tail stay honest, and so an
        // opened group shows something rather than an empty box.
        for (let i = 0; i < e.n; i++) {
          const chip = document.createElement("div");
          chip.className = "tool";
          // A transcript written before `calls` existed has only a count, so it
          // falls back to marking the first `failed` chips as errors. Imprecise,
          // and the reason the pairing above exists.
          const saved = e.calls?.[i];
          const status = saved?.status ?? (i < e.failed ? "error" : "ok");
          chip.dataset["status"] = status;
          chip.append(this.#toolIcon(status));
          const label = document.createElement("span");
          label.className = "tool-label";
          label.textContent = saved?.name || "Tool call";
          chip.append(label);
          list.appendChild(chip);
        }
        // What the visitor approved comes back with the tools that carried it out.
        for (const c of e.cards ?? []) list.appendChild(this.#resolvedCard(c));
        group.append(summary, list);
        this.#transcript.appendChild(group);
        this.#syncToolSummary(group);
      }
    }
    // Restored turns are already on disk, so an edit may unwind them.
    this.commitTurn();
    this.#scroll();
  }

  /**
   * Stands down a confirmation nobody can answer any more.
   *
   * 🔴 A CONFIRM CARD OUTLIVING ITS TURN IS A TRAP, AND VISHNU FELL INTO IT.
   *    He was asked to confirm taking a banner down, stepped away, and the
   *    stream died in the meantime. The card sat there looking exactly as
   *    live as it had a minute earlier, so he pressed Confirm. The panel
   *    dutifully wrote "Confirmed", set the input to "Running that…", and
   *    posted a decision to a turn the server had already finished. That POST
   *    404s, the answer never comes, and the panel waits forever on something
   *    that is never going to happen.
   *
   *    The button was the whole problem: it was the only thing on screen that
   *    still looked usable, so of course he used it. Worse than the wait is
   *    what he had to conclude from it, that he had approved a destructive
   *    action and had no idea whether it ran. For a product whose entire
   *    argument is "nothing happens without a yes", an ambiguous yes is the
   *    single worst state to leave somebody in.
   *
   *    Nothing was performed: the server treats a turn that ends without an
   *    answer as `timeout`, and timeout is not consent. This says so.
   */
  expirePendingConfirm(): void {
    const card = this.#pendingConfirm();
    if (!card) return;
    card.dataset["resolved"] = "true";
    card.dataset["expired"] = "true";
    card.querySelector(".confirm-actions")?.remove();

    const note = document.createElement("p");
    note.className = "confirm-outcome confirm-expired";
    // Says the state AND the remedy, and leads with the fact that matters:
    // whether the thing happened.
    note.textContent = "Not done. This expired before it was confirmed, so nothing ran. Ask again to retry.";
    card.appendChild(note);
    // The bar points at a card that is no longer waiting for anything.
    this.#syncPendingBar();
    this.#scroll();
  }

  finishTurn(): void {
    this.expirePendingConfirm();

    const group = this.#toolGroup;
    this.#toolGroup = null;
    if (!group) return;
    // A turn whose tools all vanished (none ever started) leaves an empty
    // shell that would render as a summary saying "0 tools".
    if (this.#toolCount === 0) {
      group.remove();
      return;
    }
    group.dataset["state"] = "done";
    if (this.#toolStart > 0) {
      group.dataset["elapsed"] = String((Date.now() - this.#toolStart) / 1000);
    }
    this.#syncToolSummary(group);
    this.#scroll();
  }

  /**
   * Icon, then label, nothing else. No border, no fill, no pill: a status
   * this quiet does not need a container, the same read as `Marker` in the
   * dashboard (components/marker.tsx) and, before that, an unstyled outcome
   * row in TradePilot's own side panel. Both landed on the same shape
   * independently, which is a decent sign it is the right one.
   */
  #toolIcon(status: string): SVGSVGElement {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("width", "13");
    svg.setAttribute("height", "13");
    svg.setAttribute("aria-hidden", "true");
    svg.setAttribute("class", "tool-icon");

    const paths =
      status === "ok" || status === "dry_run"
        ? ["M20 6 9 17l-5-5"]
        : status === "denied" || status === "error"
          ? ["M18 6 6 18", "m6 6 12 12"]
          : status === "unconfirmed" || status === "changed"
            ? ["M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0", "M12 8v4", "M12 16h.01"]
            : ["M21 12a9 9 0 1 1-6.219-8.56"]; // running

    for (const d of paths) {
      const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
      path.setAttribute("d", d);
      path.setAttribute("fill", "none");
      path.setAttribute("stroke", "currentColor");
      path.setAttribute("stroke-width", "2");
      path.setAttribute("stroke-linecap", "round");
      path.setAttribute("stroke-linejoin", "round");
      svg.appendChild(path);
    }
    return svg;
  }

  /**
   * 🔴 THE ACTION MAY HAVE HAPPENED. The server heard nothing back from this
   *    tool, and it changes data, so "failed" would invite the visitor to try
   *    again and make a duplicate. Marked before tool_end arrives with its
   *    plain "error", which updateToolChip then reads as this instead.
   */
  markToolUnconfirmed(callId: string): void {
    const el = this.#shadow.querySelector<HTMLElement>(`.tool[data-call="${cssEscape(callId)}"]`);
    if (el) el.dataset["unconfirmed"] = "true";
  }

  /**
   * THE VISITOR APPROVED IT AND IT WAS NOT RUN. What the action would have acted on changed
   * while the card was open, so the server held it back (docs/56, stale cards). Marked before
   * tool_end arrives as a plain "denied", which updateToolChip then reads as this instead:
   * "cancelled" beside an action the visitor had just agreed to would say they refused it.
   */
  markToolChanged(callId: string): void {
    const el = this.#shadow.querySelector<HTMLElement>(`.tool[data-call="${cssEscape(callId)}"]`);
    if (el) el.dataset["changed"] = "true";
  }

  updateToolChip(callId: string, status: string): void {
    const el = this.#shadow.querySelector<HTMLElement>(`.tool[data-call="${cssEscape(callId)}"]`);
    if (!el) return;
    if (status === "error" && el.dataset["unconfirmed"] === "true") status = "unconfirmed";
    if (status === "denied" && el.dataset["changed"] === "true") status = "changed";
    el.dataset["status"] = status;
    // A failure outweighs a success in the same turn; a simulated or cancelled one says nothing.
    if (["error", "unconfirmed", "changed"].includes(status)) this.#turnMood = "unsure";
    else if (status === "ok" && !this.#turnMood) this.#turnMood = "happy";
    el.querySelector(".tool-icon")?.replaceWith(this.#toolIcon(status));
    // Keeps the "N failed" half of the summary honest as outcomes arrive.
    const group = el.closest<HTMLElement>(".tools");
    if (group) this.#syncToolSummary(group);

    // A simulated call is labelled as such, never as done. Showing a tick for
    // something that did not happen is the exact failure the dry run exists to
    // let people check for. `ok` needs no suffix at all now: the checkmark
    // icon already says it, and repeating that in the label was the old
    // pill's only way to say "done" without one.
    if (status === "ok") return;
    const labelEl = el.querySelector<HTMLElement>(".tool-label") ?? el;
    const label = (labelEl.textContent ?? "").replace(/ · .+$/, "");
    labelEl.textContent =
      status === "dry_run"
        ? `${label} · simulated`
        : status === "denied"
          ? `${label} · cancelled`
          : status === "unconfirmed"
            ? `${label} · not confirmed`
            : status === "changed"
              ? `${label} · changed, not done`
              : `${label} · failed`;
  }

  /**
   * The confirmation card.
   *
   * Shows the ACTUAL arguments. "Confirm action?" is not consent;
   * "Cancel order 1041, Blue Widget" is.
   */
  addConfirm(
    callId: string,
    label: string,
    args: Record<string, unknown>,
    warning?: string,
    fields?: readonly InputFieldSpec[],
    display?: readonly DisplayRow[],
    debug?: DebugInfo,
  ): void {
    this.#q<HTMLElement>(".empty")?.remove();
    // Held until the visitor answers, which starts the thinking face again.
    this.#setMood("unsure");
    const card = document.createElement("div");
    card.className = "confirm";
    card.dataset["call"] = callId;
    card.setAttribute("role", "group");
    card.setAttribute("aria-label", "Confirm action");

    /**
     * 🔴 THE HEADER SAYS WHAT THE CARD IS, IN WORDS. It used to be a 3px gold
     *    rule down the left edge, whose stated job was marking this as the one
     *    card in the transcript waiting for YOU. A colour on one side is a
     *    weak way to say that: it is invisible to anyone not looking for it,
     *    it means nothing on first sight, and it carried the whole signal.
     *    Four words carry it better and survive a colourblind reader, a
     *    screenshot and a customer brand colour that happens to be gold.
     */
    const head = document.createElement("div");
    head.className = "confirm-head";

    const eyebrow = document.createElement("p");
    eyebrow.className = "confirm-eyebrow";
    eyebrow.textContent = "Needs your approval";
    head.appendChild(eyebrow);

    const title = document.createElement("p");
    title.className = "confirm-title";
    title.textContent = label;
    head.appendChild(title);
    card.appendChild(head);

    if (warning) {
      const warn = document.createElement("p");
      warn.className = "confirm-warning";
      warn.textContent = warning;
      card.appendChild(warn);
    }

    /**
     * 🔴 THE INPUTS SIT ABOVE THE ARGUMENTS, AND BOTH STAY ON SCREEN TOGETHER.
     *    A person cannot approve an action whose arguments they only ever saw
     *    three at a time, so however many fields there are, the values already
     *    known are visible on the same card as the button. docs/40 section 5.4.
     */
    /**
     * 🔴 WHAT THE CARD SHOWS, IN ONE PLACE. Rows the server built for a person when it
     *    sent any; only a widget talking to a server that does not (an older deploy) falls
     *    back to printing the arguments. The multi-step review reads the same list, so the
     *    two places a card lists values can never disagree about what they are.
     */
    const shown: Array<[string, string]> = display
      ? display.map((r): [string, string] => [r.label, formatRow(r)])
      : Object.entries(args).map(([k, v]): [string, string] => [humanise(k), typeof v === "string" ? v : JSON.stringify(v)]);
    // The stepper keys its review by name, and two rows can share a label ("Date").
    const known: Record<string, unknown> = {};
    for (const [label, value] of shown) {
      let key = label;
      for (let n = 2; key in known; n++) key = `${label} (${n})`;
      known[key] = value;
    }

    let fieldSet: Stepper | null = null;
    if (fields?.length) {
      fieldSet = buildStepper(
        fields,
        FIELDS_PER_STEP[this.#displayMode],
        known,
        () => syncAllow(),
        (_name, value) =>
          isHandle(value) ? (resolveFile(value)?.name ?? "Attached file") : describeValue(value),
      );
      card.appendChild(fieldSet.element);
    }

    const dl = document.createElement("dl");
    dl.className = "confirm-args";
    for (const [label, value] of shown) {
      const row = document.createElement("div");
      const dt = document.createElement("dt");
      dt.textContent = label;
      const dd = document.createElement("dd");
      dd.textContent = value;
      row.append(dt, dd);
      dl.appendChild(row);
    }
    // In steps, the known values appear on the review step alongside the typed
    // ones, so showing them under every step as well would only cost height.
    if (!fieldSet?.multi) card.appendChild(dl);
    // 🔴 ONLY WHEN THE SERVER SENT IT, WHICH IT DOES FOR A TEST SITE'S BUILDERS AND NO ONE
    //    ELSE. The widget does not decide who is allowed to see this: it has no way to know,
    //    and a flag it checked would be a flag a visitor could set. If the field is not in
    //    the event, there is nothing here to show. docs/56, phase 6.
    if (debug) card.appendChild(this.#debugDetails(debug));

    const actions = document.createElement("div");
    actions.className = "confirm-actions";
    const deny = document.createElement("button");
    deny.className = "btn btn-secondary";
    deny.textContent = "Cancel";
    const back = document.createElement("button");
    back.className = "btn btn-secondary";
    back.textContent = "Back";
    const allow = document.createElement("button");
    allow.className = "btn btn-primary";
    allow.textContent = "Confirm";

    /**
     * Confirm stays unavailable until every required field is answerable, and
     * the reason is shown rather than left for somebody to work out from a
     * greyed-out button.
     */
    const problemLine = document.createElement("p");
    problemLine.className = "confirm-problem";
    const syncAllow = () => {
      if (!fieldSet) return;
      const reviewing = fieldSet.onReview();
      // 🔴 Only the review step can confirm. Before it the primary button is
      //    Next, and it checks this step's fields, not the whole form.
      allow.textContent = reviewing ? "Confirm" : "Next";
      back.hidden = fieldSet.position().index === 0;
      const problem = reviewing ? fieldSet.firstProblem() : fieldSet.stepProblem();
      allow.disabled = problem !== null;
      problemLine.textContent = problem ?? "";
    };

    const resolve = (decision: "allow" | "deny") => {
      if (decision === "allow" && fieldSet && !fieldSet.onReview()) {
        fieldSet.next();
        syncAllow();
        this.#scroll();
        return;
      }
      if (decision === "allow" && fieldSet) {
        const problem = fieldSet.firstProblem();
        if (problem) {
          problemLine.textContent = problem;
          return;
        }
      }
      card.dataset["resolved"] = "true";
      /**
       * 🔴 THE FIELDS FREEZE IN THE SAME STEP THE CARD RESOLVES. Without this
       *    the transcript would keep editable inputs on a decided card, and the
       *    record of what was approved could be edited after the approval that
       *    covered it. docs/40 section 5.1.
       */
      fieldSet?.lock();
      this.#syncPendingBar();

      /**
       * 🔴 A DECIDED CARD MOVES INTO THE TURN'S RECORD RATHER THAN STAYING IN
       *    THE CONVERSATION. Once you have answered it, the question is
       *    answered: leaving a full-height card with a header, two arguments
       *    and an outcome line in the transcript pushes the actual reply off
       *    the screen, and every write leaves another one behind.
       *
       *    It is MOVED, not deleted. The arguments on that card are the
       *    evidence of what you approved, which is the whole product, so they
       *    fold into "Tools used" with everything else the turn did and come
       *    back with one press.
       *
       *    If the turn somehow has no tool group, it stays where it is. A
       *    record in the wrong place beats no record.
       */
      const list = this.#toolGroup?.querySelector(".tool-list");
      if (list) list.appendChild(card);
      const outcome = document.createElement("p");
      outcome.className = "confirm-outcome";
      // The glyph comes from CSS keyed on this, so the text stays exactly the
      // word a screen reader should read out.
      outcome.dataset["decision"] = decision;
      outcome.textContent = decision === "allow" ? "Confirmed" : "Cancelled";
      card.appendChild(outcome);
      // The input is still disabled, the turn is not done yet, but it no
      // longer needs explaining: say what is actually happening.
      this.#input.placeholder = decision === "allow" ? "Running that…" : "One moment…";
      // Called with exactly two arguments when there were no fields, so a card
      // that asks nothing behaves identically to before this existed.
      if (fieldSet) this.cb.onConfirm(callId, decision, fieldSet.values());
      else this.cb.onConfirm(callId, decision);
    };
    deny.addEventListener("click", () => resolve("deny"));
    allow.addEventListener("click", () => resolve("allow"));
    back.addEventListener("click", () => {
      fieldSet?.back();
      syncAllow();
    });

    back.hidden = true;
    actions.append(deny, back, allow);
    card.append(problemLine, actions);
    this.#transcript.appendChild(card);
    syncAllow();
    this.#syncPendingBar();
    this.#scroll();
    // The input is disabled for the whole time this card is up. Say why,
    // rather than leaving a greyed-out box with no explanation.
    this.#input.placeholder = "Waiting for you to confirm the action above…";
    /**
     * With fields, the first input takes focus: somebody has to type before any
     * button is usable, and landing them on Cancel means a keystroke to get out
     * of it. With no fields the safe option keeps focus, so a user hammering
     * Enter cannot confirm a destructive action they never read.
     */
    if (fieldSet) fieldSet.focusFirst();
    else deny.focus();
  }

  /**
   * A card that collects values and approves nothing, for a `read` tool.
   *
   * 🔴 IT EXISTS BECAUSE GATE 4 SKIPS READS. A read tool never raises a
   *    confirmation, so a missing argument on one has nowhere to be asked and
   *    used to go back through the model as prose. Same visual language as the
   *    confirmation card deliberately: this is the same kind of moment, a
   *    question with somewhere to answer it.
   */
  addInputRequest(callId: string, label: string, fields: readonly InputFieldSpec[]): void {
    this.#q<HTMLElement>(".empty")?.remove();
    const card = document.createElement("div");
    card.className = "confirm confirm-input";
    card.dataset["call"] = callId;
    card.setAttribute("role", "group");
    card.setAttribute("aria-label", "Needs more information");

    const head = document.createElement("div");
    head.className = "confirm-head";
    const eyebrow = document.createElement("p");
    eyebrow.className = "confirm-eyebrow";
    eyebrow.textContent = "Needs a detail from you";
    const title = document.createElement("p");
    title.className = "confirm-title";
    title.textContent = label;
    head.append(eyebrow, title);
    card.appendChild(head);

    const problemLine = document.createElement("p");
    problemLine.className = "confirm-problem";

    const fieldSet = buildFields(fields, () => sync());
    card.appendChild(fieldSet.element);

    const actions = document.createElement("div");
    actions.className = "confirm-actions";
    const cancel = document.createElement("button");
    cancel.className = "btn btn-secondary";
    cancel.textContent = "Cancel";
    const send = document.createElement("button");
    send.className = "btn btn-primary";
    send.textContent = "Continue";

    const sync = () => {
      const problem = fieldSet.firstProblem();
      send.disabled = problem !== null;
      problemLine.textContent = problem ?? "";
    };

    const resolve = (values: Record<string, unknown> | null) => {
      if (values && fieldSet.firstProblem()) return;
      card.dataset["resolved"] = "true";
      fieldSet.lock();
      const outcome = document.createElement("p");
      outcome.className = "confirm-outcome";
      outcome.dataset["decision"] = values ? "allow" : "deny";
      outcome.textContent = values ? "Sent" : "Cancelled";
      card.appendChild(outcome);
      const list = this.#toolGroup?.querySelector(".tool-list");
      if (list) list.appendChild(card);
      this.#input.placeholder = values ? "Running that…" : "One moment…";
      this.cb.onInput(callId, values);
    };

    cancel.addEventListener("click", () => resolve(null));
    send.addEventListener("click", () => resolve(fieldSet.values()));

    actions.append(cancel, send);
    card.append(problemLine, actions);
    this.#transcript.appendChild(card);
    sync();
    this.#scroll();
    this.#input.placeholder = "Waiting for the details above…";
    fieldSet.focusFirst();
  }

  /**
   * Labels the session as test or dry run, permanently.
   *
   * Not dismissible on purpose. Mistaking a simulated session for a real one is
   * exactly the failure the environment split exists to prevent, so the label
   * outlives any single message.
   */
  /**
   * The assistant's own name, from the boot call.
   *
   * 🔴 Until this existed the header showed whatever `data-verb-name` said, or
   *    "Ask AI". The name a customer typed into the dashboard reached nothing at
   *    all: not the header, not the greeting, not the model. Setting a name and
   *    watching nothing change is the kind of thing that makes somebody stop
   *    trusting the rest of the settings.
   */
  setAssistantName(name: string): void {
    if (!name.trim()) return;
    this.#assistantName = name;
    const title = this.#shadow.querySelector<HTMLElement>(".title");
    if (title) title.textContent = name;
    this.#launcher.setAttribute("aria-label", `Ask ${name}`);
    this.#repaintAvatars();
  }

  /**
   * The first thing anyone sees, and the cards that tell them what to ask.
   *
   * A user who does not know what to ask asks nothing, and an assistant nobody
   * speaks to produces no evidence for the owner. Rendered once, before any
   * turn, and cleared as soon as somebody types.
   */
  /**
   * Records what the panel WOULD have opened with, without drawing it.
   *
   * For the reload case: a visitor with a saved conversation gets their
   * transcript back rather than a greeting, but New chat still has to be able
   * to put the greeting and the cards on screen afterwards.
   */
  rememberOpening(greeting: string, starters: readonly Starter[]): void {
    this.#opening = { greeting, starters };
  }

  showOpening(greeting: string, starters: readonly Starter[]): void {
    // Kept so New chat can put the same panel back without a network call.
    this.rememberOpening(greeting, starters);
    if (greeting.trim()) this.addMessage("assistant", greeting, null);
    if (starters.length === 0) return;

    const wrap = document.createElement("div");
    wrap.className = "starters";
    /**
     * 🔴 8, MATCHING WHAT THE DASHBOARD LETS SOMEBODY SAVE. It was 6 while
     *    the settings form and its validation both allowed 8, so a site
     *    owner who wrote seven or eight cards had the last of them silently
     *    dropped here, with nothing on either screen saying so. Suggested
     *    starters cap at 4 (see the orchestrator's starters/suggest); this
     *    higher bound is only for someone who deliberately wrote their own.
     */
    for (const s of starters.slice(0, 8)) {
      const href = linkStarter(s);

      /**
       * 🔴 A LINK CARD IS AN ANCHOR, NOT A BUTTON WITH A CLICK HANDLER. Middle
       *    click, ctrl-click and "open in new tab" all work for free, and a
       *    screen reader announces it as a link, which is what it is. A button
       *    that navigates is a button that lies about what it will do.
       *
       * 🔴 rel="noopener noreferrer" IS REQUIRED, NOT TIDINESS. Without
       *    noopener the opened page gets a handle on the customer's window
       *    through window.opener and can navigate it somewhere else.
       */
      if (href) {
        const link = document.createElement("a");
        link.className = s.highlight ? "starter starter-link starter-glow" : "starter starter-link";
        link.textContent = s.title;
        link.href = href;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        wrap.appendChild(link);
        continue;
      }

      const card = document.createElement("button");
      card.type = "button";
      card.className = s.highlight ? "starter starter-glow" : "starter";
      card.textContent = s.title;
      card.addEventListener("click", () => {
        wrap.remove();
        /**
         * The title is the fallback, not an empty string. `prompt` is optional
         * on the type because a link card has none, and a row that reaches
         * here has one by construction; if a hand-written or older row does
         * not, sending what the visitor actually pressed is the honest reading
         * and cannot post an empty message.
         */
        const ask = s.prompt || s.title;

        /**
         * 🔴 A WRITTEN ANSWER NEVER REACHES THE MODEL OR THE SERVER. No turn,
         *    no token, no cost, and the same words every time. The card that
         *    explains what the product is should not be able to answer that
         *    question differently on a Tuesday.
         *
         *    The exchange is therefore NOT in the server's history either, so
         *    a written answer has to stand on its own: a follow-up starts the
         *    conversation from nothing, and the model will not know what
         *    "that" referred to. Written answers say the whole thing.
         */
        if (s.answer) {
          this.addMessage("user", ask);
          /**
           * 🔴 IT RUNS ON TIMERS AFTER THIS CLICK HANDLER HAS RETURNED, AND
           *    NOTHING AWAITS IT. Without this catch, anything that threw part
           *    way through the performance would surface as an unhandled
           *    rejection in the CUSTOMER'S console, on the customer's page,
           *    from a widget whose whole pitch is that it does not break their
           *    site. Found on 2026-08-26 as a test that failed after teardown,
           *    which is the same shape: the DOM went away mid-animation.
           *
           *    The recovery hands the composer back, because the alternative
           *    is a panel stuck disabled behind a thinking row that never
           *    resolves, which is worse than a missing answer. It is itself
           *    guarded: if the panel is what disappeared, there is nothing to
           *    hand back to.
           */
          void this.#playWritten(s.answer).catch(() => {
            try {
              this.hideThinking();
              this.setBusy(false);
            } catch {
              // The panel is gone. Nothing to restore, and nothing to report to.
            }
          });
          return;
        }

        this.cb.onSend(ask);
        this.addMessage("user", ask);
      });
      wrap.append(card);
    }
    this.#transcript.append(wrap);
    this.#scroll();
  }

  /**
   * A written answer arriving the way a real one does: a pause, then text.
   *
   * 🔴 THE PACING IS PRESENTATION, NOT A CLAIM. Nothing here pretends the
   *    model was involved; the site's own greeting says the cards are written
   *    in advance. What the delay buys is that a written answer and a real one
   *    read as the same thing, so the panel does not lurch between two
   *    behaviours depending on which card was pressed. A 900-character block
   *    appearing instantly is also simply harder to start reading.
   *
   * 🔴 Someone who has asked their operating system for reduced motion gets
   *    the answer, not the performance. No delay at all.
   */
  async #playWritten(answer: string): Promise<void> {
    const run = ++this.#answerRun;
    const superseded = () => run !== this.#answerRun;

    this.setBusy(true);
    this.showThinking();

    if (reduceMotion()) {
      this.appendDelta(answer);
    } else {
      await sleep(WRITTEN_THINK_MS);
      if (superseded()) return;

      const parts = streamChunks(answer);
      // A long answer arrives faster per word rather than taking proportionally
      // longer, so the last card is not four times the wait of the first.
      const tick = Math.min(45, Math.max(10, Math.round(WRITTEN_STREAM_MS / parts.length)));
      for (const part of parts) {
        this.appendDelta(part);
        await sleep(tick);
        if (superseded()) return;
      }
    }

    this.endStream();
    this.hideThinking();
    this.setBusy(false);
    this.cb.onAnswered();
  }

  /**
   * Start over: clears the transcript and puts the opening back.
   *
   * 🔴 A CLEAR THAT ONLY CLEARS THE SCREEN IS THE WORST OF BOTH. The server
   *    keeps the conversation, so the next question is answered with the old
   *    one still in view of the model while the user is looking at an empty
   *    panel. cb.onNewChat is what actually drops it; see index.ts.
   */
  newChat(): void {
    // Stops a written answer that is still arriving. See #answerRun.
    this.#answerRun++;
    this.#transcript.replaceChildren();
    this.#rated.clear();
    this.#thinking = null;
    this.#streaming = null;
    this.#toolGroup = null;
    this.#turnMood = null;
    this.#setMood("idle");
    this.setBusy(false);
    // The transcript just went, and any waiting card went with it, so the bar
    // must not be left pointing at nothing.
    this.#syncPendingBar();
    this.cb.onNewChat();

    // The placeholder goes back first: addMessage removes it again the moment
    // the greeting lands, and a site with neither greeting nor cards is left
    // with the same empty panel it booted with rather than nothing at all.
    const empty = document.createElement("p");
    empty.className = "empty";
    empty.textContent = "Ask a question, or ask me to do something.";
    this.#transcript.appendChild(empty);

    if (this.#opening) this.showOpening(this.#opening.greeting, this.#opening.starters);
    if (this.#open) this.#input.focus();
  }

  /**
   * 🔴 Hides the launcher entirely rather than disabling it.
   *
   *    A disabled button on somebody else's admin panel is a question with no
   *    answer: the visitor cannot know why, and the site owner gets the support
   *    ticket. On a site that declared it is behind a login, the assistant
   *    simply is not there until a token says who is asking, and then it
   *    appears. Nothing is refused because nothing was offered.
   */
  setHidden(hidden: boolean): void {
    this.#hidden = hidden;
    this.#syncLauncherVisibility();
    if (hidden) this.close();
  }

  /**
   * Whether the default floating launcher shows at all, independent of
   * #hidden (identity-gating) and of the panel being open.
   *
   * 🔴 THIS EXISTS FOR "COMPLETE CONTROL", NOT AS A HALF-MEASURE.
   *
   *    A site that wants to drive the assistant entirely from its OWN button
   *    (`<button data-verb-open>`, see index.ts) does not want our floating
   *    circle competing with it in the corner. Setting this to false removes
   *    it outright rather than leaving a second, redundant trigger on the
   *    page nobody asked for.
   */
  setLauncherVisible(visible: boolean): void {
    this.#showLauncher = visible;
    this.#syncLauncherVisibility();
  }

  /** The one place launcher visibility is decided, from every condition that can hide it. */
  #syncLauncherVisibility(): void {
    const visible = this.#showLauncher && !this.#hidden && !this.#open;
    this.#launcher.style.display = visible ? "" : "none";
  }

  /**
   * Which corner the launcher sits in, and which side the panel unfolds
   * from. A dialog-mode panel is centred regardless, so this only visibly
   * changes anything in the default "panel" display mode.
   */
  setPosition(position: LauncherPosition): void {
    this.#panel.dataset["position"] = position;
    this.#launcher.dataset["position"] = position;
    // The bubble points at the button, so it goes wherever the button goes.
    this.#teaser.dataset["position"] = position;
  }

  /**
   * 🔴 A REAL SECOND LAYOUT, NOT A CSS SKIN ON THE FIRST ONE.
   *
   *    "panel" is a full-height drawer with somewhere for a long-running
   *    conversation to live alongside whatever the visitor is looking at,
   *    which is the shape an ACTING assistant needs: a confirmation card for
   *    "reject this submission" is more useful next to the submissions table
   *    than on top of it. "dialog" is a centred, fixed-size box, the Cmd+K
   *    shape a pure answer-and-close tool like Kapa's uses, where there is
   *    nothing on the page worth keeping visible underneath it.
   *
   *    Both read from the same transcript, the same composer, the same
   *    confirmation cards; only the CSS in styles.ts positions and sizes the
   *    `.panel` element differently per `data-display-mode`. Switching modes
   *    never loses the conversation.
   *
   * Push-layout is switched off outright in dialog mode: a centred box has
   * nothing beside it to push, and forcing a margin change for a mode that
   * does not dock to an edge would be motion with no purpose.
   */
  /**
   * Push the page aside, or cover it, from the dashboard's Theme tab
   * (migration 0037). Arrives after boot with the config, so it can land while
   * the panel is already open: turning push off takes our margin away at once,
   * and turning it on applies it at once, rather than waiting for a reopen.
   */
  setPushLayout(on: boolean): void {
    if (on === this.#pushLayout) return;
    if (!on) document.body.style.removeProperty("margin-right");
    this.#pushLayout = on;
    if (on && this.#open) this.#markPageState(true);
  }

  setDisplayMode(mode: DisplayMode): void {
    this.#displayMode = mode;
    this.#panel.dataset["displayMode"] = mode;
    // A site switched to dialog while the panel is already open must get its
    // backdrop, and one switched away from it must lose it.
    this.#syncScrim();
  }

  /**
   * The backdrop is on only when a dialog is open.
   *
   * 🔴 NEVER BEHIND THE DRAWER. The drawer pushes the page aside instead of
   *    covering it, so that somebody can keep looking at the thing they are
   *    asking about; dimming it would take exactly that back. Dialog mode has
   *    no such promise to keep, and without a backdrop it reads as a box that
   *    failed to dock rather than a modal.
   */
  #syncScrim(): void {
    this.#scrim.dataset["open"] = String(this.#open && this.#displayMode === "dialog");
  }

  /**
   * One colour in, a full accessible palette out. See
   * @verb/theme#deriveBrandTokens for the contrast math: the text drawn on
   * the launcher, send and confirm buttons is computed against the chosen
   * colour, not assumed, so an arbitrary brand colour cannot silently ship
   * an unreadable button.
   *
   * Applied to `this.host`, not `:host` in the stylesheet: a custom property
   * set as an inline style on the shadow HOST is inherited into the shadow
   * tree same as any CSS custom property, and it takes precedence over the
   * adopted stylesheet's own `:host` rule without needing `!important`.
   */
  setBrandColor(hex: string): void {
    const tokens = deriveBrandTokens(hex);
    for (const [prop, value] of Object.entries(tokens)) {
      if (value) this.host.style.setProperty(prop, value);
    }
  }

  /**
   * The colour of the widget itself, as distinct from the colour of its
   * buttons. One hex in, both surfaces, both borders and all three text
   * shades out; see @verb/theme#deriveSurfaceTokens for why the text colour
   * is computed against the chosen ground rather than fixed.
   *
   * Set on the host as inline custom properties, exactly as setBrandColor
   * does, so it inherits into the shadow tree and outranks the adopted
   * stylesheet without !important.
   */
  /**
   * The site's plan allows removing "Powered by Verb" and its owner did
   * (/v1/config's showBranding). Anything else, including no answer, shows it.
   */
  setShowBranding(show: boolean): void {
    this.#brandingOff = !show;
    const line = this.#footer.querySelector<HTMLElement>(".powered");
    if (show) {
      this.checkBranding();
      return;
    }
    line?.remove();
  }

  /**
   * Puts "Powered by Verb" back if the page removed, hid or changed it, and
   * returns whether it had to. The panel's own markup never carries a style or
   * hidden attribute on these three elements, so either one is a page's doing.
   */
  checkBranding(): boolean {
    if (this.#brandingOff) return false;
    const footer = this.#footer;
    const line = footer.querySelector<HTMLElement>(".powered");
    const link = line?.querySelector("a") ?? null;
    const shown = (el: Element) => {
      if (el.hasAttribute("style") || el.hasAttribute("hidden")) return false;
      const cs = getComputedStyle(el);
      return cs.display !== "none" && cs.visibility !== "hidden" && !(Number.parseFloat(cs.opacity) < 0.5);
    };
    const sheetIntact =
      this.#shadow.adoptedStyleSheets.length === 1 &&
      this.#shadow.adoptedStyleSheets[0] === this.#sheet &&
      this.#sheet.cssRules.length === this.#sheetRules;
    const intact =
      footer.parentElement === this.#footerHome &&
      !!line &&
      line.parentElement === footer &&
      line.textContent?.replace(/\s+/g, " ").trim() === POWERED_TEXT &&
      link?.getAttribute("href") === "https://askverb.com" &&
      sheetIntact &&
      shown(footer) &&
      shown(line) &&
      shown(link);
    if (intact) return false;

    if (!sheetIntact) {
      this.#sheet.replaceSync(styles);
      this.#sheetRules = this.#sheet.cssRules.length;
      this.#shadow.adoptedStyleSheets = [this.#sheet];
    }
    footer.removeAttribute("style");
    footer.removeAttribute("hidden");
    if (footer.parentElement !== this.#footerHome) this.#footerHome.append(footer);
    const fresh = document.createElement("div");
    fresh.className = "powered";
    fresh.innerHTML = POWERED;
    line?.remove();
    footer.prepend(fresh);
    this.onBrandingRestored?.();
    return true;
  }

  setBackgroundColor(hex: string): void {
    const tokens = deriveSurfaceTokens(hex);
    for (const [prop, value] of Object.entries(tokens)) {
      if (value) this.host.style.setProperty(prop, value);
    }
  }

  /**
   * One chat bubble's fill, and the text colour that follows from it.
   *
   * 🔴 NOTHING IS SET WHEN THE COLOUR DOES NOT PARSE, which is what keeps the
   *    derived value in play. The CSS reads
   *    `var(--verb-bubble-user, var(--verb-surface-alt))`, so an absent token
   *    falls through to the shade that follows the panel background. Writing a
   *    partial or invalid value here would break that chain in the one way CSS
   *    does not recover from: a declaration containing an unresolvable var()
   *    is invalid at computed-value time and resolves to unset, not to the
   *    rule before it. That is how the launcher lost its fill on 2026-09-07.
   */
  setBubbleColor(role: BubbleRole, hex: string): void {
    const tokens = deriveBubbleTokens(role, hex);
    for (const [prop, value] of Object.entries(tokens)) {
      if (value) this.host.style.setProperty(prop, value);
    }
  }

  /**
   * The animated ring behind the launcher and behind highlighted starters.
   *
   * 🔴 "off" REMOVES THE ATTRIBUTE RATHER THAN SETTING IT TO "off". Every rule
   *    in styles.ts matches the bare `[data-glow]`, which is what keeps the
   *    glow CSS from needing the value repeated in a dozen selectors. An
   *    attribute of "off" would switch all of it on.
   *
   * It goes on BOTH the launcher and the panel because they are siblings in
   * the shadow root, not ancestor and descendant: the starters live in the
   * panel, so the panel is where they inherit the colour ramp from.
   */
  setGlow(glow: Glow): void {
    for (const el of [this.#launcher, this.#panel]) {
      if (glow === "off") delete el.dataset["glow"];
      else el.dataset["glow"] = glow;
    }
  }

  setMode(environment: "test" | "live", dryRun: boolean): void {
    this.#shadow.querySelector(".mode-banner")?.remove();
    if (environment === "live" && !dryRun) return;

    const el = document.createElement("div");
    el.className = "mode-banner";
    el.dataset["dry"] = String(dryRun);
    el.setAttribute("role", "status");

    const dot = document.createElement("span");
    dot.className = "mode-banner-dot";
    const label = document.createElement("strong");
    label.textContent = dryRun ? "Dry run" : "Test mode";
    const detail = document.createElement("span");
    /**
     * 🔴 A TEST SITE THAT IS NOT SIMULATING HAS TO SAY SO, HERE, NOT ONLY IN
     *    THE DASHBOARD. "not your live site" is true and reassuring and was
     *    written when a test site could not execute anything; once real
     *    execution existed it read as "nothing here counts" at the exact moment
     *    the assistant was about to change a row. Migration 0005 calls somebody
     *    believing production was simulated the worst failure this product could
     *    have, and this is that failure with the two sides swapped.
     *
     *    The dashboard toggle is invisible from inside the panel, and the panel
     *    is where the person is looking when it matters.
     */
    detail.textContent = dryRun ? "nothing will actually change" : "actions will really run";

    el.append(dot, label, detail);
    this.#panel.insertBefore(el, this.#transcript);
    this.#dryRun = dryRun;
  }

  addNotice(text: string, kind: "info" | "error" = "info"): void {
    if (kind === "error") this.#setMood("unsure", 2500);
    const el = document.createElement("p");
    el.className = "notice";
    el.dataset["kind"] = kind;
    el.textContent = text;
    this.#transcript.appendChild(el);
    this.#scroll();
  }

  /**
   * The remaining characters, once there are few enough to care about.
   *
   * 🔴 SILENT TRUNCATION IS THE FAILURE THIS AVOIDS. `maxlength` alone stops
   *    the typing but says nothing, so somebody pasting a long log watches the
   *    end of it vanish with no explanation and no idea how much was lost.
   */
  #syncCounter(): void {
    const left = MAX_MESSAGE - this.#input.value.length;
    const show = left <= COUNTER_VISIBLE_AT;
    this.#counter.hidden = !show;
    if (show) {
      this.#counter.textContent = left === 0 ? "Limit reached" : `${left} left`;
      this.#counter.dataset["full"] = String(left === 0);
    }
  }

  setBusy(busy: boolean): void {
    // Read before disabling: disabling the focused input is what takes focus away.
    if (busy) this.#typing = this.#shadow.activeElement === this.#input;
    this.#send.disabled = busy;
    this.#input.disabled = busy;
    // Editing an earlier question while an answer is still arriving would race
    // the transcript it is about to truncate. The buttons go away for the few
    // seconds a turn takes.
    this.#panel.dataset["busy"] = String(busy);
    if (busy) return;
    // The turn is over. Whatever explained the wait, a pending confirm, a
    // "running that" note, no longer applies.
    this.#input.placeholder = "Ask anything…";

    /**
     * 🔴 BACK TO THE INPUT WHEN THE ANSWER LANDS (Vishnu, 2026-09-25: "the
     *    focus from the type bar is off, I need to click again"). Disabling a
     *    focused input drops focus to the page, and enabling it again does not
     *    give it back, so every follow-up question cost a click.
     *
     *    Only when they were typing when the turn started, and only if focus
     *    has not gone anywhere since: somebody who clicked into the host page
     *    meanwhile keeps their focus, since stealing it from the customer's own
     *    page is worse than the bug. Not on touch screens either, where focus
     *    opens the keyboard over the answer they are about to read.
     */
    const typing = this.#typing;
    this.#typing = false;
    const idle = document.activeElement === null || document.activeElement === document.body;
    if (typing && this.#open && idle && finePointer()) this.#input.focus();
  }

  /**
   * Is a turn still in flight?
   *
   * Read off the same attribute setBusy writes rather than a second field, so
   * there is one source of truth. Used by adoptIdentity to tell the difference
   * between clearing an idle conversation and interrupting a live one, which
   * are the same wipe and very different news for the person watching.
   */
  isBusy(): boolean {
    return this.#panel.dataset["busy"] === "true";
  }

  #submit(): void {
    const text = this.#input.value.trim();
    if (!text) return;
    this.#input.value = "";
    this.#input.style.height = "auto";
    this.#syncCounter();
    this.addMessage("user", text);
    this.cb.onSend(text);
  }

  /**
   * The assistant's face.
   *
   * Its initial by default, from the name the customer set, so it is right on
   * every site without anyone configuring anything. `data-verb-avatar` on the
   * script tag replaces it with an image.
   */
  #avatar(live = false): HTMLElement {
    const el = document.createElement("div");
    el.className = "avatar";
    el.setAttribute("aria-hidden", "true");

    // Moving in the header and the thinking row only: every reply wearing the same moving face
    // would blink in unison down the transcript.
    if (this.#avatarBlob) {
      el.classList.add("avatar-blob");
      el.appendChild(drawBlob(this.#avatarBlob, live, this.#mood));
      return el;
    }

    if (this.#avatarUrl) {
      const img = document.createElement("img");
      img.src = this.#avatarUrl;
      img.alt = "";
      // A broken image URL must not leave a blank circle. Fall back to the
      // initial, which cannot fail.
      img.addEventListener("error", () => {
        this.#avatarUrl = "";
        this.#repaintAvatars();
      });
      el.appendChild(img);
      return el;
    }

    const initial = document.createElement("span");
    initial.textContent = (this.#assistantName.trim()[0] ?? "A").toUpperCase();
    el.appendChild(initial);
    return el;
  }

  /**
   * The visitor's own face, and deliberately NOT configurable.
   *
   * 🔴 A GENERIC GLYPH, NEVER A PHOTO OR AN INITIAL. Verb knows who the
   *    visitor is on an identified site, so it could put their initial here.
   *    It must not: the panel renders on the customer's own page, where a
   *    shared screen, a support agent looking over a shoulder or a screenshot
   *    in a bug report all put that letter in front of somebody it was not
   *    meant for. This marks WHICH SIDE of the conversation a message is on,
   *    which is the entire job, and it needs no personal data to do it.
   *
   *    Also why there is no setter for it. The site owner customises the
   *    assistant's face because the assistant is theirs. The visitor is not.
   */
  #userAvatar(): HTMLElement {
    const el = document.createElement("div");
    el.className = "avatar avatar-user";
    el.setAttribute("aria-hidden", "true");

    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("width", "14");
    svg.setAttribute("height", "14");
    for (const d of ["M20 21a8 8 0 0 0-16 0", "M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8"]) {
      const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
      path.setAttribute("d", d);
      path.setAttribute("fill", "none");
      path.setAttribute("stroke", "currentColor");
      path.setAttribute("stroke-width", "2");
      path.setAttribute("stroke-linecap", "round");
      path.setAttribute("stroke-linejoin", "round");
      svg.appendChild(path);
    }
    el.appendChild(svg);
    return el;
  }

  /**
   * The name and the picture both arrive after the first rows are drawn.
   *
   * 🔴 EXCLUDES THE USER'S AVATAR. This replaces every matching node with a
   *    freshly built ASSISTANT avatar, so a bare `.avatar` selector would turn
   *    every visitor glyph into the assistant's face the moment /v1/config
   *    resolved. The header's own avatar is included on purpose: it is the
   *    assistant's, and it has to follow the same name and picture.
   */
  #repaintAvatars(): void {
    for (const old of this.#shadow.querySelectorAll(".avatar:not(.avatar-user)")) {
      const wasHeader = old.classList.contains("avatar-header");
      const live = wasHeader || old.closest(".row")?.querySelector(".thinking") != null;
      const next = this.#avatar(live);
      if (wasHeader) next.classList.add("avatar-header");
      old.replaceWith(next);
      const blob = next.querySelector<SVGSVGElement>("svg.blob");
      if (blob && wasHeader) followPointer(blob);
    }
  }

  setAvatar(url: string): void {
    if (!url.trim()) return;
    this.#avatarUrl = url;
    this.#repaintAvatars();
    this.#paintLauncherFace();
  }

  /** Wear a blob instead of the picture (migration 0067); empty goes back to the picture. */
  setAvatarBlob(seed: string): void {
    if (seed === this.#avatarBlob) return;
    if (seed) registerBlobProperties();
    this.#avatarBlob = seed;
    this.#repaintAvatars();
    this.#paintLauncherFace();
  }

  /**
   * Put the site's own picture on the button, or take it off again.
   *
   * 🔴 THE MARK STAYS IN THE MARKUP AND IS ONLY HIDDEN. Replacing the label's
   *    contents would mean rebuilding the glow layers above it, and a broken
   *    image URL would leave an empty circle with nothing to fall back to.
   *    Hidden, the fallback is one class away and cannot fail.
   */
  setLauncherAvatar(on: boolean): void {
    this.#launcherAvatar = on;
    this.#paintLauncherFace();
  }

  /** The face on the button at 1 to 2 times its 52px (migration 0068); anything else is ignored. */
  setLauncherSize(size: number): void {
    if (!(size >= 1 && size <= 2)) return;
    this.#launcherSize = size;
    this.#paintLauncherFace();
  }

  /**
   * A short line that leans out of the button before anybody clicks it, so a
   * visitor does not have to guess that the corner is worth opening.
   *
   * 🔴 IT ASKS ONCE AND THEN NEVER AGAIN. A bubble that comes back every
   *    page view is not an invitation, it is a pop-up, and it would be OUR
   *    pop-up on somebody else's product. Dismissing it, or simply opening the
   *    panel, settles it for good on this browser.
   *
   * 🔴 EMPTY MEANS OFF. There is no second switch to disagree with the
   *    text (migration 0058), so a site turns this off by clearing the line.
   */
  setLauncherTeaser(text: string | null): void {
    this.#teaserText = text?.trim() || null;
    if (!this.#teaserText || this.#teaserSilenced()) return;
    if (this.#teaserTimer !== null) return;
    /**
     * Not on the first frame. A line that appears at the same moment as the
     * page is read as part of the page loading and skipped; a beat later it
     * is read as something arriving, which is the whole point of it.
     */
    this.#teaserTimer = setTimeout(() => {
      this.#teaserTimer = null;
      this.#showTeaser();
    }, TEASER_DELAY_MS);
  }

  #showTeaser(): void {
    if (!this.#teaserText || this.#open || this.#teaserSilenced()) return;
    // Never over a button that is not there: hidden, switched off, or moved
    // into the customer's own layout, where a fixed corner points at nothing.
    if (this.#launcher.style.display === "none") return;
    if (this.#launcher.dataset["inline"] === "true") return;
    this.#teaser.querySelector(".teaser-open")!.textContent = this.#teaserText;
    this.#teaser.hidden = false;
    this.#teaserShown = true;
    // Once per visit. A reload must not repeat it; a new tab may.
    try {
      sessionStorage.setItem(TEASER_SESSION_KEY, "1");
    } catch {
      // Then it may repeat on a reload, which is the harmless failure.
    }
    // Two frames apart, or the transition has nothing to animate from.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      this.#teaser.dataset["show"] = "true";
    }));
  }

  /**
   * @param persist whether this counts as the visitor having SEEN it.
   *
   * 🔴 ONLY A BUBBLE THAT WAS ON SCREEN COUNTS AS ASKED AND ANSWERED. The
   *    first version wrote the flag on every open(), which quietly meant that
   *    anybody who had ever opened the panel was settled for good BEFORE the
   *    site switched a teaser on, and would never see it. That is most of a
   *    live site's visitors, and it is exactly what happened on the Cal demo
   *    the first time: the line was set, the config served it, and nothing
   *    appeared, because the panel had been opened earlier in the day
   *    (2026-10-06).
   *
   *    Opening the panel before it ever appeared just cancels this page view.
   *    Nothing is remembered, so the next visit still gets its one chance.
   */
  #dismissTeaser(persist: boolean): void {
    if (this.#teaserTimer !== null) {
      clearTimeout(this.#teaserTimer);
      this.#teaserTimer = null;
    }
    delete this.#teaser.dataset["show"];
    this.#teaser.hidden = true;
    if (!persist) return;
    /**
     * Storage can throw outright in a private window or with site data
     * blocked, and this runs on somebody else's page: a remembered dismissal
     * is a nicety, and it is never worth taking their page down for.
     */
    try {
      localStorage.setItem(TEASER_QUIET_KEY, String(Date.now() + TEASER_QUIET_MS));
    } catch {
      // Then it asks once more next time, which is the harmless failure.
    }
  }

  /**
   * Whether the bubble must stay quiet: already shown in this visit, or
   * answered recently enough that asking again would be nagging.
   */
  #teaserSilenced(): boolean {
    try {
      if (sessionStorage.getItem(TEASER_SESSION_KEY) === "1") return true;
    } catch {
      // Unreadable storage is not a reason to stay silent.
    }
    try {
      // A stored value that is not a number at all (the retired "1", or
      // anything a person typed in) is treated as no suppression, never as
      // forever: silence has to be something we wrote and dated.
      const until = Number(localStorage.getItem(TEASER_QUIET_KEY));
      // A quiet time longer than TEASER_QUIET_MS was written under the old month-long rule and is
      // ignored, so browsers holding one hear the bubble again now rather than in November.
      return Number.isFinite(until) && until > 0 && Date.now() < until && until - Date.now() <= TEASER_QUIET_MS;
    } catch {
      return false;
    }
  }

  /**
   * Keeps the open panel inside the part of the screen the phone keyboard has
   * not covered.
   *
   * 🔴 position:fixed MEASURES THE LAYOUT VIEWPORT, WHICH A KEYBOARD DOES NOT
   *    SHRINK (Vishnu, 2026-10-06, on askverb.com: "when I open the keyboard
   *    it shows like this, I can scroll it up a bit"). The panel is pinned top
   *    and bottom, so with the keyboard up it stays taller than the visible
   *    area, the composer ends up underneath the keys, and the browser scrolls
   *    the whole sheet to reach it. The thing that DOES shrink is the visual
   *    viewport, so the panel is measured against that instead.
   *
   * 🔴 ONLY WHILE OPEN, AND ONLY WHERE THE API EXISTS. A closed panel has
   *    nothing to size, and visualViewport is absent on older browsers, where
   *    the variables stay unset and the stylesheet's own 100dvh applies. Every
   *    listener is removed on close: this runs on somebody else's page and
   *    must leave nothing behind.
   */
  /**
   * Undoes everything open() set up for a phone: the keyboard tracking, the page
   * lock, and the two variables the stylesheet reads. One call, so close() cannot
   * remember three of the four and leave a host page stuck.
   */
  #releasePhone(): void {
    this.#untrackViewport?.();
    this.#untrackViewport = null;
    this.#unlockPage?.();
    this.#unlockPage = null;
    this.#panel.style.removeProperty("--verb-vv-height");
    this.#panel.style.removeProperty("--verb-vv-top");
  }

  /**
   * Stops the page behind a full-screen sheet from moving under the visitor's
   * finger.
   *
   * 🔴 NOTHING HELD THE PAGE STILL. The sheet is position:fixed, but a drag on
   *    its header or its composer is a drag on the document, so the host page
   *    scrolled behind it. On a phone that makes the browser's address bar slide
   *    in and out, which changes the viewport height UNDER the sheet, so it
   *    resized and jumped as the finger moved; with the keyboard up, the same
   *    drag pans the visible area. This shipped in 0.0.18 and it took a real
   *    phone to see it (Vishnu, 2026-10-08): jsdom has no scrolling at all.
   *
   * 🔴 ONLY THE FULL-SCREEN SHEET. A docked drawer on a wide screen leaves the
   *    page beside it alive, which is the whole point of a drawer, so it is
   *    never locked. The width is read once, at open, and the unlock is paired
   *    with it, so turning the phone while open cannot leave a page stuck.
   *
   * 🔴 THE HOST'S OWN VALUES COME BACK EXACTLY. A page that already sets
   *    overflow or overscroll-behavior inline, important or not, gets precisely
   *    that back, not "unset". This runs on somebody else's page and leaves
   *    nothing behind.
   *
   * 🔴 A DRAG THAT HAS NOTHING TO SCROLL IS CANCELLED, ONE THAT DOES IS NOT.
   *    overflow:hidden alone is not honoured for touch on every browser, so the
   *    touchmove is cancelled where no element under the finger can actually
   *    scroll: the header, the footer, the buttons. The transcript, a long draft
   *    and a wide table scroll as normal, and a two-finger pinch is the
   *    visitor's own and is never touched.
   */
  #lockPage(): void {
    if (this.#unlockPage || window.innerWidth > 640) return;
    const roots = [document.documentElement, document.body];
    const props = ["overflow", "overscroll-behavior"] as const;
    const saved = roots.map((el) =>
      props.map((p) => ({ value: el.style.getPropertyValue(p), priority: el.style.getPropertyPriority(p) })),
    );
    for (const el of roots) {
      el.style.setProperty("overflow", "hidden", "important");
      el.style.setProperty("overscroll-behavior", "none", "important");
    }

    const onTouchMove = (e: Event): void => {
      if (((e as TouchEvent).touches?.length ?? 1) > 1) return;
      if (!this.#touchCanScroll(e)) e.preventDefault();
    };
    // passive:false is the whole point: a passive listener cannot cancel a drag.
    this.#panel.addEventListener("touchmove", onTouchMove, { passive: false });

    this.#unlockPage = () => {
      this.#panel.removeEventListener("touchmove", onTouchMove);
      roots.forEach((el, i) => {
        props.forEach((p, j) => {
          const s = saved[i]![j]!;
          if (s.value) el.style.setProperty(p, s.value, s.priority);
          else el.style.removeProperty(p);
        });
      });
    };
  }

  /** Whether anything under the finger, inside the sheet, can scroll. */
  #touchCanScroll(e: Event): boolean {
    for (const node of e.composedPath()) {
      if (node === this.#panel) break;
      if (!(node instanceof HTMLElement)) continue;
      // Cheap test first: most elements do not overflow, and reading computed
      // style on every one of them for every touch event would not be free.
      if (node.scrollHeight <= node.clientHeight + 1 && node.scrollWidth <= node.clientWidth + 1) continue;
      const { overflowX, overflowY } = getComputedStyle(node);
      if (/(auto|scroll)/.test(`${overflowX} ${overflowY}`)) return true;
    }
    return false;
  }

  #trackViewport(): void {
    const vv = window.visualViewport;
    if (!vv || this.#untrackViewport) return;
    const apply = () => {
      /**
       * 🔴 MEASURED BEFORE THE SHEET CHANGES SIZE. The keyboard shrinks the
       *    visible area, the transcript shrinks with it, and whatever was at the
       *    bottom ends up under the composer: the visitor's own question, cut in
       *    half, on the very screen where they are about to type the next one
       *    (Vishnu, 2026-10-08, from his phone). Somebody reading the latest
       *    message stays on it; somebody who scrolled up to reread is left alone.
       */
      const tr = this.#transcript;
      const pinned = tr.scrollHeight - tr.scrollTop - tr.clientHeight < 48;
      this.#panel.style.setProperty("--verb-vv-height", `${Math.round(vv.height)}px`);
      // offsetTop is how far the visible area has itself been scrolled away
      // from the layout viewport's top, which is where the sheet must start.
      this.#panel.style.setProperty("--verb-vv-top", `${Math.round(vv.offsetTop)}px`);
      if (pinned) tr.scrollTop = tr.scrollHeight;
    };
    apply();
    vv.addEventListener("resize", apply);
    vv.addEventListener("scroll", apply);
    this.#untrackViewport = () => {
      vv.removeEventListener("resize", apply);
      vv.removeEventListener("scroll", apply);
    };
  }

  #paintLauncherFace(): void {
    const label = this.#launcher.querySelector(".launcher-label");
    if (!label) return;
    const existing = label.querySelector(".launcher-img");
    const wanted = this.#launcherAvatar && Boolean(this.#avatarBlob || this.#avatarUrl);
    // The face's size (migration 0068), and the teaser rides above it. Our own pill keeps both as
    // they were, so a size set for a face never moves anything on a site without one.
    const px = Math.round(52 * this.#launcherSize);
    if (wanted) {
      this.#launcher.style.setProperty("--verb-face-size", `${px}px`);
      this.#teaser.style.setProperty("--verb-face-lift", `${px + 10}px`);
    } else {
      this.#launcher.style.removeProperty("--verb-face-size");
      this.#teaser.style.removeProperty("--verb-face-lift");
    }

    if (!wanted) {
      existing?.remove();
      this.#launcher.classList.remove("has-img", "img-ready");
      return;
    }
    // A blob has nothing to download, so the button is the blob at once, eyes on the cursor.
    if (this.#avatarBlob) {
      if (existing?.getAttribute("data-seed") === this.#avatarBlob) return;
      existing?.remove();
      const blob = drawBlob(this.#avatarBlob, true, this.#mood);
      blob.classList.add("launcher-img");
      blob.setAttribute("data-seed", this.#avatarBlob);
      label.appendChild(blob);
      this.#launcher.classList.add("has-img", "img-ready");
      followPointer(blob);
      return;
    }
    // From a blob back to a picture: img-ready waits for this picture's own load event.
    if (existing && existing.tagName !== "IMG") {
      existing.remove();
      this.#launcher.classList.remove("img-ready");
    } else if (existing) {
      if (existing.getAttribute("src") !== this.#avatarUrl) existing.setAttribute("src", this.#avatarUrl);
      return;
    }
    const img = document.createElement("img");
    img.className = "launcher-img";
    img.alt = "";
    // Listeners before src: a cached image can finish before the next line.
    img.addEventListener("load", () => this.#launcher.classList.add("img-ready"));
    // A URL that does not load must leave the button looking like a button,
    // not like a hole. Same rule as the header avatar above.
    img.addEventListener("error", () => {
      img.remove();
      this.#launcher.classList.remove("has-img", "img-ready");
    });
    img.src = this.#avatarUrl;
    this.#launcher.classList.add("has-img");
    // A cached image may already be done, and fires no load event if so.
    if (img.complete && img.naturalWidth > 0) this.#launcher.classList.add("img-ready");
    label.appendChild(img);
  }

  /**
   * 🔴 EDITING IS NOT A COSMETIC UNDO.
   *
   *    Rewriting a question that has already been answered leaves the original
   *    question and its answer in the server's history. The model would then
   *    read the mistake, the answer to the mistake, and the correction, and the
   *    most common result is that it answers the old one. So the edit takes the
   *    branch with it: every row from here down goes, and the server is told how
   *    many committed turns to unwind.
   */
  #editButton(row: HTMLElement, bubble: HTMLElement): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "edit";
    btn.setAttribute("aria-label", "Edit and send again");
    btn.title = "Edit and send again";
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 16 16");
    svg.setAttribute("width", "13");
    svg.setAttribute("height", "13");
    svg.setAttribute("aria-hidden", "true");
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", "M11.2 2.3l2.5 2.5-7.6 7.6-3.1.6.6-3.1z");
    path.setAttribute("fill", "none");
    path.setAttribute("stroke", "currentColor");
    path.setAttribute("stroke-width", "1.4");
    path.setAttribute("stroke-linejoin", "round");
    svg.appendChild(path);
    btn.appendChild(svg);
    btn.addEventListener("click", () => this.#beginEdit(row, bubble));
    return btn;
  }

  #beginEdit(row: HTMLElement, bubble: HTMLElement): void {
    if (row.dataset["editing"] === "true") return;
    row.dataset["editing"] = "true";

    const original = bubble.textContent ?? "";
    const editor = document.createElement("div");
    editor.className = "editor";

    const field = document.createElement("textarea");
    field.className = "editor-input";
    field.value = original;
    field.rows = Math.min(6, original.split("\n").length + 1);

    const actions = document.createElement("div");
    actions.className = "editor-actions";
    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.className = "btn btn-secondary";
    cancel.textContent = "Cancel";
    const save = document.createElement("button");
    save.type = "button";
    save.className = "btn btn-primary";
    save.textContent = "Send again";
    actions.append(cancel, save);
    editor.append(field, actions);

    const stop = (): void => {
      row.dataset["editing"] = "false";
      editor.remove();
      bubble.hidden = false;
    };

    const commit = (): void => {
      const text = field.value.trim();
      if (!text) return;
      if (text === original) {
        stop();
        return;
      }
      const rewind = this.#committedTurnsFrom(row);
      this.#truncateFrom(row);
      this.addMessage("user", text);
      // The original goes with it, so the server can check it is cutting the
      // turn we think it is rather than trusting a count.
      this.cb.onEdit(text, rewind, original);
    };

    cancel.addEventListener("click", stop);
    save.addEventListener("click", commit);
    field.addEventListener("keydown", (e) => {
      // Escape must not reach the panel's own handler, or cancelling an edit
      // would close the whole assistant and lose the conversation.
      if (e.key === "Escape") {
        e.stopPropagation();
        stop();
      }
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        commit();
      }
    });

    bubble.hidden = true;
    row.insertBefore(editor, bubble.nextSibling);
    field.focus();
    field.setSelectionRange(field.value.length, field.value.length);
  }

  #userRows(): HTMLElement[] {
    return [...this.#transcript.querySelectorAll<HTMLElement>('.row[data-role="user"]')];
  }

  /** How many saved turns this edit throws away, itself included. */
  #committedTurnsFrom(row: HTMLElement): number {
    const rows = this.#userRows();
    const from = rows.indexOf(row);
    if (from === -1) return 0;
    return rows.slice(from).filter((r) => r.dataset["committed"] === "true").length;
  }

  #truncateFrom(row: HTMLElement): void {
    let node: Element | null = row;
    while (node) {
      const next: Element | null = node.nextElementSibling;
      node.remove();
      node = next;
    }
    this.#thinking = null;
    this.#streaming = null;
  }

  #trapFocus(e: KeyboardEvent): void {
    const focusable = [
      ...this.#panel.querySelectorAll<HTMLElement>(
        'button:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
      ),
    ].filter((el) => el.offsetParent !== null || el === this.#input);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = this.#shadow.activeElement;

    if (e.shiftKey && active === first) {
      e.preventDefault();
      last?.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first?.focus();
    }
  }

  #scroll(): void {
    this.#transcript.scrollTop = this.#transcript.scrollHeight;
  }

  #q<T extends HTMLElement>(sel: string): T {
    return this.#shadow.querySelector(sel) as T;
  }
}

/**
 * Puts one line's inline markup into `into`. Currently that means **bold**.
 *
 * 🔴 NEVER innerHTML. It splits on the literal ** delimiter and builds
 *    <strong> elements with createElement and createTextNode, so a tool result
 *    that happens to contain `<img onerror="...">` lands as an inert text node.
 *    A bold span cannot smuggle a tag or an attribute, because nothing here
 *    ever parses HTML: it parses delimiters and nothing else. Every branch in
 *    the block renderer below routes its text through this function, so that
 *    property holds for headings and list items too rather than only for the
 *    paragraph case it was originally written for.
 */
/** How long the dots show before a written answer starts arriving. */
const WRITTEN_THINK_MS = 420;
/** Roughly how long a written answer takes to finish, whatever its length. */
const WRITTEN_STREAM_MS = 1400;

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Whether this visitor has asked their system for less movement.
 *
 * In a try/catch because matchMedia is missing or partial in enough embedded
 * webviews that assuming it would break the widget on somebody's page, and the
 * safe answer to "I do not know" here is to animate as normal.
 */
function reduceMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/**
 * A mouse or trackpad, not a finger. Focusing an input on a touch screen opens
 * the keyboard, so the widget only moves focus by itself where that costs
 * nothing. Unknown counts as a mouse: that is the case the bug was reported on.
 */
function finePointer(): boolean {
  try {
    return !window.matchMedia("(pointer: coarse)").matches;
  } catch {
    return true;
  }
}

/**
 * A written answer, cut into the pieces it arrives in.
 *
 * Two words at a time, with their trailing whitespace attached, because that
 * is roughly what a real stream delivers and because splitting per character
 * makes markdown flicker: renderRich re-renders the whole raw string on every
 * delta, so a half-typed ** is a visible stutter twice per emphasis.
 */
function streamChunks(text: string): string[] {
  const tokens = text.match(/\S+\s*/g) ?? [text];
  const out: string[] = [];
  for (let i = 0; i < tokens.length; i += 2) out.push(tokens.slice(i, i + 2).join(""));
  return out;
}

/**
 * 🔴 A BACKSLASH ESCAPE LEAKED INTO A REAL ANSWER, 2026-09-24: "\*These are
 *    platform pricing ratios..." rendered with the backslash still attached,
 *    on Resuelve's own footnote-style disclaimer. `\*` is the model writing a
 *    literal asterisk the CommonMark way, specifically so it is NOT read as
 *    the start of a bullet or of **bold**; this renderer understood neither
 *    of those meanings, so the backslash, which exists only to be invisible,
 *    was the one character that showed.
 *
 * Only a backslash before ASCII punctuation counts, matching CommonMark's own
 * escape rule exactly. That is what keeps this safe on a Windows path or a
 * regex a user pastes: `\Users`, `\d+`, a backslash before a LETTER, is left
 * alone, because nothing here is a punctuation character.
 */
const MD_ESCAPE = /\\([!"#$%&'()*+,\-./:;<=>?@[\]^_`{|}~])/g;
function unescapeMd(text: string): string {
  return text.replace(MD_ESCAPE, "$1");
}

function renderInline(into: HTMLElement, text: string): void {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  for (const [i, part] of parts.entries()) {
    if (!part) continue;
    const clean = unescapeMd(part);
    if (i % 2 === 1) {
      const strong = document.createElement("strong");
      strong.textContent = clean;
      into.appendChild(strong);
    } else {
      into.appendChild(document.createTextNode(clean));
    }
  }
}

/**
 * True when `line` is a GFM table separator: cells made of only `-`, with an
 * optional `:` on either end for alignment, and nothing else. This is what
 * turns a plain `|`-led line into a recognised table rather than a stray pipe
 * in prose: a header alone is not enough, the separator underneath it is the
 * signal a model would not write by accident.
 */
function isTableRule(line: string): boolean {
  const cells = splitTableRow(line);
  return cells.length > 0 && cells.every((c) => /^:?-+:?$/.test(c.trim()));
}

function splitTableRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith("|")) s = s.slice(1);
  if (s.endsWith("|")) s = s.slice(0, -1);
  return s.split("|");
}

function appendTable(into: HTMLElement, rows: string[][]): void {
  const [header, ...body] = rows;
  if (!header) return;
  const wrap = document.createElement("div");
  wrap.className = "md-table-wrap";
  const table = document.createElement("table");
  table.className = "md-table";
  const thead = document.createElement("thead");
  const headRow = document.createElement("tr");
  for (const cell of header) {
    const th = document.createElement("th");
    renderInline(th, cell.trim());
    headRow.appendChild(th);
  }
  thead.appendChild(headRow);
  const tbody = document.createElement("tbody");
  for (const row of body) {
    const tr = document.createElement("tr");
    for (const cell of row) {
      const td = document.createElement("td");
      renderInline(td, cell.trim());
      tr.appendChild(td);
    }
    tbody.appendChild(tr);
  }
  table.append(thead, tbody);
  wrap.appendChild(table);
  into.appendChild(wrap);
}

/**
 * Renders assistant text: headings, bullet lists, tables, and **bold**.
 *
 * 🔴 HEADINGS AND BULLETS WERE ARRIVING AS LITERAL `###` AND `-`, 2026-08-20.
 *    Only ** was handled, on the reasoning that bold is the one thing the
 *    model habitually writes. That held until mirchi35's assistant answered a
 *    question about app configuration and produced a real structured answer:
 *    a `### Current app version configuration` heading over a list of
 *    `- **Latest version**: 1.0.15` bullets. The bold inside each bullet
 *    rendered correctly, which made it worse, not better: the reader sees the
 *    formatting working and concludes the stray `###` and dashes are a bug,
 *    because they are.
 *
 *    A model asked about anything with more than one field WILL reach for a
 *    list. Treating that as an exotic case was the mistake.
 *
 * 🔴 TABLES ADDED 2026-09-24: a comparison answer arrived as literal
 *    `| Cost component | GPT-5.6-Luna | ... |` rows, unrendered, on Resuelve's
 *    own AI-comparison feature. A `|`-led line is only ever treated as a table
 *    when the line under it is a separator row (`isTableRule`); that pairing
 *    is the one thing a model would not write by accident, so a stray pipe in
 *    prose stays prose. The prompt now tells the model to write comparisons as
 *    a standard GFM table for exactly this reason.
 *
 * 🔴 STILL NOT A MARKDOWN PARSER, and deliberately not. It handles four
 *    constructs by looking at how a line STARTS, and everything else, links,
 *    code fences, images, falls through to plain text. Every addition here is
 *    a new way for model output to become markup, so the bar is "the model
 *    writes this constantly and it looks broken without it", which headings,
 *    bullets and tables now clearly pass and the rest does not.
 *
 * Re-parses the FULL text every call rather than diffing the new delta in,
 * because a streamed answer routinely splits "**0 pending" and " subs**"
 * across two SSE deltas, and there is no correct place to render a lone `**`
 * as bold until the half that closes it has arrived. The same is true of a
 * bullet, or a table row, whose line has not finished yet: until the
 * separator row under a header is complete, `isTableRule` says no and the
 * header renders as a plain line, exactly like a half-written `###` does.
 */
function renderRich(el: HTMLElement, text: string): void {
  el.replaceChildren();

  // Paragraph text is accumulated rather than emitted per line, so a wrapped
  // sentence stays one block and `white-space: pre-wrap` renders its internal
  // newlines. Emitting a div per line would double every line break.
  let para: string[] = [];
  let list: HTMLElement | null = null;

  const flushPara = () => {
    if (para.length === 0) return;
    const p = document.createElement("div");
    p.className = "md-p";
    renderInline(p, para.join("\n"));
    el.appendChild(p);
    para = [];
  };
  const endList = () => {
    list = null;
  };

  const lines = text.split("\n");
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? "";
    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    const bullet = /^\s*[-*]\s+(.+)$/.exec(line);

    if (line.trim().startsWith("|") && isTableRule(lines[i + 1] ?? "")) {
      flushPara();
      endList();
      const rows = [splitTableRow(line)];
      i += 2; // the header line, plus the separator line just confirmed above
      while ((lines[i] ?? "").trim().startsWith("|")) {
        rows.push(splitTableRow(lines[i] ?? ""));
        i++;
      }
      i--; // the for loop's own i++ accounts for the last row consumed
      appendTable(el, rows);
      continue;
    }

    if (heading) {
      flushPara();
      endList();
      const h = document.createElement("div");
      h.className = "md-h";
      renderInline(h, heading[2] ?? "");
      el.appendChild(h);
      continue;
    }

    if (bullet) {
      flushPara();
      if (!list) {
        list = document.createElement("ul");
        list.className = "md-ul";
        el.appendChild(list);
      }
      const li = document.createElement("li");
      renderInline(li, bullet[1] ?? "");
      list.appendChild(li);
      continue;
    }

    // A blank line closes whatever block was open. Inside a paragraph it is
    // also a real break the writer intended, so it is kept there.
    if (!line.trim()) {
      endList();
      flushPara();
      continue;
    }

    endList();
    para.push(line);
  }

  flushPara();
}

function humanise(s: string): string {
  return s.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
}

/**
 * A duration a person would say out loud.
 *
 * 🔴 NOBODY SAYS "84 SECONDS". Past a minute the unit stops being seconds and
 *    becomes minutes and seconds, because that is the shape a reader converts
 *    it to anyway. Under a minute one decimal is the useful precision; over it,
 *    whole seconds, since a tenth of a second in two minutes is noise.
 */
export function duration(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const mins = Math.floor(seconds / 60);
  const rest = Math.round(seconds - mins * 60);
  // 119.6s rounds to 120, which must read as 2m rather than 1m 60s.
  return rest === 60 ? `${mins + 1}m` : rest === 0 ? `${mins}m` : `${mins}m ${rest}s`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] ?? c,
  );
}

function cssEscape(s: string): string {
  return s.replace(/["\\]/g, "\\$&");
}

/** A value as a person reads it on the review step. */
function describeValue(value: unknown): string {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}
